// Shapes exchanged between the web app and the API. No field here ever carries a password, password hash,
// session token or one-time code: the API has no way to return them.
import * as Exact from "./decimal.js";
import type { PermissionKey } from "./permission-keys.js";
import type { SourceRef } from "./finance.js";

export interface Named { id: string; nameEn: string; nameAr: string }
export interface BranchDto extends Named {}
export interface WarehouseDto extends Named { branchId: string }
export interface TreasuryDto extends Named { branchId: string; kind: "cash" | "bank" }
export interface PrinterDto { id: string; name: string }

export interface Scope {
  /** null for administrators: every branch, warehouse and treasury of the organization. */
  branchId: string | null;
  warehouseIds: string[] | null;
  treasuryIds: string[] | null;
}

export interface MeDto {
  user: { id: string; uid: number; name: string; phone: string; administrator: boolean;
    /** The business owner (migration 031): the only user who posts invoices to the accounts. Always an administrator. */
    owner: boolean };
  /** onboardingPending: a business that signed up and has not finished the onboarding wizard (administrators finish it). */
  /** ownerAssigned: the organization has an active business owner; while it has none, nobody posts to the accounts. */
  organization: { id: string; nameEn: string; nameAr: string; ownerAssigned: boolean; onboardingPending?: boolean; /** The business holds migrated legacy documents (Legacy history). */ legacyHistory?: boolean };
  permissions: string[];
  scope: Scope;
  /** Posting policies (migration 034): each type's mode, and what this user may post (the owner: every type) or manage. */
  posting: MePosting;
  /** A business that signed up: its package state and features (migration 043). Null when Delta provisioned it. */
  package: import("./packages.js").MePackageDto | null;
  /** A public demo business (migration 055): when it ends. Absent for every real business. */
  demo?: import("./demo.js").MeDemoDto;
}

// ---------------------------------------------------------------- posting policies (migration 034, rules PP-1 to PP-6)

/** The documents a posting policy covers: purchases, sales and their refunds, the seven voucher kinds and journal entries. */
export type PostingDoc = "sale" | "sale_refund" | "purchase" | "purchase_refund" | "expense" | "deposit" | "withdraw" | "cash_transfer" | "collect" | "payment" | "opening_balance" | "journal";
export const POSTING_DOCS: readonly PostingDoc[] = ["sale", "sale_refund", "purchase", "purchase_refund", "expense", "deposit", "withdraw", "cash_transfer", "collect", "payment", "opening_balance", "journal"];
/** automatic: finishing a document posts it; manual: it waits for the owner or a user allowed to post its type. */
export type PostingMode = "automatic" | "manual";
/** What each type did before migration 034, and still does until the owner changes it. */
export const DEFAULT_POSTING_MODE: Record<PostingDoc, PostingMode> = {
  sale: "manual", sale_refund: "manual", purchase: "manual", purchase_refund: "manual",
  expense: "automatic", deposit: "automatic", withdraw: "automatic", cash_transfer: "automatic", collect: "automatic", payment: "automatic", opening_balance: "automatic", journal: "automatic"
};
export type PostingRight = PostingDoc | "manage";
export interface MePosting { modes: Record<PostingDoc, PostingMode>; post: PostingDoc[]; manage: boolean }
export interface PostingPolicyRow {
  docType: PostingDoc; mode: PostingMode; isDefault: boolean; updatedAt: string | null; updatedByName: string | null;
  /** Documents of this type waiting for posting, and their total (kept apart from every posted figure). */
  pending: { count: number; total: string };
}
export interface PostingUserRow { id: string; name: string; administrator: boolean; owner: boolean; active: boolean; rights: PostingRight[] }
export interface PostingPoliciesDto { policies: PostingPolicyRow[]; users: PostingUserRow[]; canManage: boolean; owner: boolean; ownerAssigned: boolean }
export interface PostingPolicyInput { mode: PostingMode }
export interface PostingRightsInput { rights: PostingRight[] }
export interface PendingDocDto {
  family: "invoice" | "voucher" | "journal"; docType: PostingDoc; id: string; code: string | null; at: string; total: string;
  party: string | null; statement: string; waitingSince: string; byName: string | null;
}
export interface PostingBatchInput { items: { family: "invoice" | "voucher" | "journal"; id: string }[] }
export interface PostingBatchDto { results: { id: string; ok: boolean; code?: string; message?: string }[]; posted: number }

export interface UserDto {
  id: string;
  uid: number;
  name: string;
  phone: string;
  administrator: boolean;
  /** The business owner (migration 031). */
  owner: boolean;
  active: boolean;
  branchId: string | null;
  warehouseIds: string[];
  treasuryIds: string[];
  ticketPrinterId: string | null;
  ticketCopies: number;
  ticketPrinting: boolean;
  permissionCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface UserListDto { users: UserDto[]; seats: { active: number; limit: number } }

export interface UserInput {
  name: string;
  phone: string;
  /** Required when creating; optional when updating (empty keeps the current password). */
  password?: string;
  branchId: string;
  warehouseIds: string[];
  treasuryIds: string[];
  ticketPrinterId?: string | null;
  ticketCopies: number;
  ticketPrinting: boolean;
  active: boolean;
}

export interface UserPermissionsDto { userId: string; administrator: boolean; permissions: string[] }

export interface FormOptionsDto { branches: BranchDto[]; warehouses: WarehouseDto[]; treasuries: TreasuryDto[]; printers: PrinterDto[] }

export interface AuditEntryDto {
  id: string;
  at: string;
  actorUserId: string | null;
  action: string;
  entity: string | null;
  entityId: string | null;
  ip: string | null;
  before: unknown;
  after: unknown;
  meta: unknown;
}

/** Error codes returned as { error: { code, message, missing? } }; the web app translates the code. */
export type ApiErrorCode =
  | "unauthenticated" | "invalid_credentials" | "too_many_attempts" | "forbidden" | "out_of_scope"
  | "not_found" | "validation" | "phone_taken" | "seat_limit" | "cannot_change_self" | "cannot_change_admin"
  | "grant_beyond_own" | "target_has_more" | "name_taken" | "in_use" | "file_too_large" | "invalid_image" | "code_taken" | "barcode_taken"
  | "posted_locked" | "not_posted" | "already_reversed" | "reversal_locked" | "document_entry" | "legacy_entry" | "cutover_opening" | "rule_not_approved" | "stock_moved_since" | "setting_missing" | "has_refunds" | "not_refundable" | "not_postable" | "has_settlements" | "period_locked" | "asset_locked" | "not_due" | "shift_open" | "treasury_shift_open" | "shift_closed" | "no_shift" | "request_conflict"
  | "original_printed" | "no_original" | "job_conflict" | "wrong_state" | "reference_taken"
  | "csrf" | "unsupported_media_type" | "internal"
  // Signup, onboarding, entitlements and the platform portal (migration 027)
  | "not_correctable" | "pos_sale" | "pos_draft" | "correction_confirm_required" | "correction_open" | "import_duplicate" | "import_invalid"
  | "signups_closed" | "manual_activation_not_allowed" | "email_taken" | "account_pending" | "organization_suspended" | "verification_blocked"
  | "code_invalid" | "code_expired" | "too_many_codes" | "already_verified" | "not_verified" | "entitlement_limit" | "onboarding_incomplete"
  | "subscription_overlap" | "plan_code_taken" | "feature_not_in_package" | "trial_already_used" | "not_trial" | "package_inactive" | "package_kind" | "offer_not_trial" | "nothing_to_renew" | "package_blocked" | "already_enforced" | "would_block" | "legacy_rules"
  // POS devices, grants and the sales inbox (migration 021)
  | "activation_invalid" | "device_unauthorized" | "device_deactivated" | "device_suspended" | "device_not_pending"
  | "grant_denied" | "sale_conflict" | "payload_hash_mismatch" | "already_resolved" | "recovery_invalid" | "device_not_active"
  // Owner-controlled posting and editing completed invoices (migration 031)
  | "owner_only" | "not_completed" | "original_not_posted" | "amend_confirm_required" | "tobacco_total_confirm_required" | "tobacco_account_unusable" | "amend_refused" | "shift_closed_payments" | "last_owner" | "owner_unassigned"
  // Nested chart of accounts and account codes (migration 033)
  | "no_code_suggestion" | "code_released" | "chart_too_large" | "move_confirm_required" | "expense_tax_changed" | "expense_type_changed" | "expense_account_not_allowed" | "expense_tax_default_not_allowed" | "import_has_problems" | "tobacco_settings_not_allowed" | "price_edit_not_allowed" | "below_cost_approval_required" | "below_cost_approval_invalid" | "below_cost_approval_used" | "below_cost_approval_changed" | "below_cost_self_approval" | "not_below_cost" | "demo_disabled" | "demo_full" | "demo_rate_limited" | "demo_real_session" | "demo_locked" | "demo_ended" | "demo_quota" | "approval_decided" | "owner_rights" | "template_conflicts" | "template_changed" | "template_invalid" | "template_version" | "alongside_required" | "numbering_incompatible" | "class_locked" | "group_cycle" | "group_class"
  // Posting policies and posting rights (migration 034)
  | "awaiting_posting"
  // Resellers and reseller billing (migration 048)
  | "reseller_suspended" | "reseller_action_not_allowed" | "reseller_package_not_allowed" | "reseller_trial_limit" | "reseller_customer_limit"
  | "reseller_extension_too_long" | "reseller_terms_missing" | "reseller_currency_locked" | "reseller_assigned_elsewhere" | "reseller_code_taken"
  // Add business and owner setup (migration 053)
  | "delivery_unavailable" | "same_name" | "setup_done" | "setup_invalid"
  | "credit_exceeds_charge" | "already_reversed_payment" | "setup_code_invalid" | "password_not_set" | "not_eligible"
  // Offline POS sales (migration 058)
  | "offline_not_enabled" | "offline_ticket_invalid" | "shift_not_for_device" | "shift_not_cashiers" | "offline_sales_pending" | "not_appliable"
  // Super Admin data management (migration 059)
  | "reauth_failed" | "confirm_name_mismatch" | "scope_invalid" | "plan_stale" | "purge_running" | "export_required"
  // Coordinated data removal (migration 060).
  | "not_ready" | "freeze_required" | "freeze_active" | "no_freeze" | "export_stale" | "purge_not_ready" | "demo_business" | "register_not_waiting" | "note_required"
  | "business_frozen" | "register_closed" | "invoice_not_sale" | "invoice_linked";

/** What still points at a record that cannot be deleted or moved, by kind, with counts. */
export type References = Partial<Record<
  "warehouses" | "treasuries" | "users" | "deletedUsers" | "items" | "itemUnits"
  | "accounts" | "groups" | "taxTypes" | "itemKinds" | "journalLines" | "vouchers" | "parties" | "organization" | "stockDocuments" | "invoices" | "assets" | "assetInvoices" | "assetKinds" | "partners" | "printers", number>>;

export interface ApiError { error: { code: ApiErrorCode; message: string; missing?: string[]; fields?: Record<string, string>; limit?: number; references?: References; amend?: InvoiceAmendSummary; belowCost?: BelowCostDetails; modules?: string[]; feature?: string } }

// ---------------------------------------------------------------- Phase 2: settings master data
// Field names follow the audited screens and legacy records (erp-audit/evidence/screens.json, schema.txt).

export interface CurrencyDto { code: string; nameEn: string; nameAr: string }

export interface OrganizationInput {
  nameAr: string; nameEn: string; taxNumber: string; commercialRecord: string; note: string;
  phone: string; landline: string; fax: string; email: string;
  country: string; city: string; neighborhood: string; street: string;
  /** The national address's building number (4 digits) and postal code (5 digits), for e-invoices (rule Z-5). Absent keeps the stored value. */
  buildingNumber?: string; postalCode?: string;
  /** The compliant system that issues the organization's legal tax invoices while Delta's receipts are non-fiscal (docs/non-fiscal-pos.md). Absent keeps the stored value. */
  legalInvoiceSystem?: string;
  /**
   * Default customer and supplier accounts (legacy Setting.customerAccountId / supplierAccountId; rule C-1, provisional).
   * Absent keeps the stored value, null clears it. A new client or supplier takes the default when no account is chosen.
   */
  customerAccountId?: string | null; supplierAccountId?: string | null;
  /**
   * Stock accounts (rule S-5, provisional): the account beginning inventory is credited to, and the account count
   * differences are posted to. Same convention: absent keeps, null clears.
   */
  openingStockAccountId?: string | null; stockDifferenceAccountId?: string | null;
}
export interface LogoDto { contentType: string; size: number; updatedAt: string }
export interface OrganizationDto extends Omit<OrganizationInput, "customerAccountId" | "supplierAccountId" | "openingStockAccountId" | "stockDifferenceAccountId" | "buildingNumber" | "postalCode" | "legalInvoiceSystem"> {
  id: string; buildingNumber: string; postalCode: string; legalInvoiceSystem: string; logo: LogoDto | null; updatedAt: string;
  customerAccountId: string | null; customerAccount: AccountRef | null; supplierAccountId: string | null; supplierAccount: AccountRef | null;
  openingStockAccountId: string | null; openingStockAccount: AccountRef | null; stockDifferenceAccountId: string | null; stockDifferenceAccount: AccountRef | null;
}

/** Settings › Period lock (decision P-14; rules K-1 to K-4, provisional). */
export interface PeriodLockInput { lockedUntil: string | null; reason?: string }
export interface PeriodLockChange { at: string; before: string | null; after: string | null; reason: string; by: string }
export interface PeriodLockDto {
  lockedUntil: string | null; lockedAt: string | null; lockedBy: { id: string; name: string } | null;
  /** Today in Saudi Arabia: the latest date the lock may be set to. */
  today: string;
  history: PeriodLockChange[];
}

export interface BranchInput { nameAr: string; nameEn: string; phone: string; addressAr: string; addressEn: string; monthlyExpense: number | string; currency: string }
export interface BranchRecordDto extends Omit<BranchInput, "monthlyExpense"> { monthlyExpense: number; id: string; createdAt: string; updatedAt: string }

export interface BarcodeSettingsInput {
  titleAr: string; titleEn: string;
  size: number; height: number; width: number;
  marginTop: number; marginBottom: number; marginRight: number; marginLeft: number;
  showTitle: boolean; showText: boolean; showName: boolean; showPrice: boolean;
  weightActive: boolean; weightLeftFlag: number; weightCodeLength: number; weightQtyLength: number; weightCheckLength: number;
}
export interface BarcodeSettingsDto extends BarcodeSettingsInput { branchId: string; updatedAt: string }

/** Phase 4 links are optional in requests: absent keeps the stored value, null clears it. */
export interface WarehouseInput { branchId: string; nameAr: string; nameEn: string; accountId?: string | null }
export interface WarehouseRecordDto extends Omit<WarehouseInput, "accountId"> {
  id: string; branchNameEn: string; branchNameAr: string; accountId: string | null; account: AccountRef | null; createdAt: string; updatedAt: string;
  /** Items with stock in the warehouse and the value of its stock (phase 8). */
  itemsInStock: number; stockValue: Decimal;
}

export interface TreasuryInput {
  nameAr: string; nameEn: string; branchId: string; kind: "cash" | "bank"; accountNumber: string; iban: string;
  accountId?: string | null; commission?: DecimalInput; commissionAccountId?: string | null; commissionTaxTypeId?: string | null;
}
export interface TreasuryRecordDto extends Omit<TreasuryInput, "accountId" | "commission" | "commissionAccountId" | "commissionTaxTypeId"> {
  id: string; branchNameEn: string; branchNameAr: string;
  accountId: string | null; account: AccountRef | null; commission: Decimal; commissionAccountId: string | null; commissionAccount: AccountRef | null;
  commissionTaxTypeId: string | null; commissionTaxType: (Named & { percentage: Decimal }) | null;
  createdAt: string; updatedAt: string;
}

// ---------- Phase 3: items, categories, units, clients and suppliers ----------
/** Decimals travel as exact text ("12.5000") in responses; requests accept a JSON number or decimal text. */
export type Decimal = string;
export type DecimalInput = number | string;

export interface NamedInput { nameAr: string; nameEn: string }
/** missingName: a category or unit migrated with that name missing (I-13). nameEn or nameAr then shows the other
 *  language's name in its place, for display only; the stored name stays empty until an authorized user adds it. */
export interface CategoryDto extends NamedInput { id: string; itemsCount: number; createdAt: string; updatedAt: string; missingName: "en" | "ar" | null }
export interface UnitDto extends NamedInput { id: string; itemsCount: number; createdAt: string; updatedAt: string; missingName: "en" | "ar" | null }

export interface ItemUnitInput {
  /** Id of an existing unit row of this item, to keep it; absent for a new row. */
  id?: string;
  unitId: string; pieces: DecimalInput;
  /** Empty wholesale or half-wholesale price takes the retail price (prototype rule, erp-items.js). */
  priceWholesale: DecimalInput | ""; priceHalf: DecimalInput | ""; priceRetail: DecimalInput;
  barcodes: string[];
}
export interface ItemInput {
  categoryId: string; itemKindId: string; taxTypeId: string; code: string; nameAr: string; nameEn: string; descriptionAr: string; descriptionEn: string;
  discount: DecimalInput; minQty: DecimalInput; priceIncludeTax: boolean; quickList: boolean; units: ItemUnitInput[];
  /** Subject to the tobacco municipality fee (migration 049); absent keeps the stored value. */
  tobaccoFee?: boolean;
}
export interface ItemUnitDto { id: string; unitId: string; unitNameEn: string; unitNameAr: string; pieces: Decimal; priceWholesale: Decimal; priceHalf: Decimal; priceRetail: Decimal; barcodes: string[] }
export interface ItemDto {
  id: string; categoryId: string; categoryNameEn: string; categoryNameAr: string; code: string; nameAr: string; nameEn: string;
  itemKindId: string | null; itemKind: (Named & { hasQty: boolean }) | null; taxTypeId: string | null; taxType: (Named & { percentage: Decimal }) | null;
  descriptionAr: string; descriptionEn: string; discount: Decimal; minQty: Decimal; priceIncludeTax: boolean; quickList: boolean;
  /** Subject to the tobacco municipality fee (migration 049). */
  tobaccoFee: boolean;
  image: { contentType: string; size: number; updatedAt: string } | null; units: ItemUnitDto[]; createdAt: string; updatedAt: string;
  /** How a migrated item departs from Delta's item rules as stored (D-13, D-14); empty for items entered in Delta. A "no_code" item has code "". */
  legacyFlags: ("no_code" | "long_name" | "no_base_unit" | "repeated_units" | "no_units")[];
}
export interface ItemListDto { id: string; code: string; nameAr: string; nameEn: string; categoryId: string; categoryNameEn: string; categoryNameAr: string; itemKindId: string | null; itemKindNameEn: string | null; itemKindNameAr: string | null; hasQty: boolean | null; discount: Decimal; minQty: Decimal; quickList: boolean; hasImage: boolean; /** The image's version (its update time) for /api/items/:id/image?v=…; null without one. */ imageAt: string | null; unitsCount: number; barcodes: string[]; /** Stock on hand in base units, in the warehouses in scope (null for items whose kind holds no stock). */ stockQty: Decimal | null }
export interface ItemFormOptions { categories: CategoryDto[]; units: UnitDto[]; itemKinds: (Named & { hasQty: boolean })[]; taxTypes: (Named & { percentage: Decimal })[] }

export type PartyKind = "customer" | "supplier";
export type Pricing = "wholesale" | "half" | "retail";
/**
 * `accountId` is the party's account index (legacy Account.ChartOfAccountId; rule C-1, provisional). On create, absent
 * or null takes the organization's default for the kind; on update, absent keeps the stored account and null clears it.
 */
export interface PartyInput { name: string; phone: string; address: string; maxWithdrawalLimit: DecimalInput; taxNumber: string; commercialRecord: string; other: string; pricing?: Pricing; accountId?: string | null }
export interface PartyDto {
  id: string; kind: PartyKind; name: string; phone: string; address: string; maxWithdrawalLimit: Decimal; taxNumber: string; commercialRecord: string; other: string; pricing: Pricing | null;
  accountId: string | null; account: AccountRef | null;
  /** Debit minus credit of the posted lines tagged with the party, on any account (rule L-5). */
  balance: Decimal;
  createdAt: string; updatedAt: string;
}

// ---------- Phase 4: accounting configuration ----------
// Chart of accounts (legacy ChartOfAccountLevel1/2/3), cost centers, tax types and item kinds. Configuration only:
// no balance, posting or tax calculation exists, so none of these shapes carries one.

/** A level-3 account as other records show it. */
export interface AccountRef { id: string; code: string; nameEn: string; nameAr: string }
/** The five fixed level-1 groups (legacy ChartOfAccountLevel1). */
export interface Level1Dto { id: number; nameEn: string; nameAr: string }

/**
 * A group of accounts (legacy level-2 type; since rule H-1 groups nest to any depth up to 10 under a class). `code` is
 * optional: existing groups have none and none is invented.
 */
export interface GroupRef { id: string; code: string | null; nameEn: string; nameAr: string }
/** A group in a selector: its parent, its depth under the class (1: directly under it) and its path of names, class first. */
export interface GroupOption extends GroupRef { level1Id: number; parentId: string | null; depth: number; pathEn: string; pathAr: string }

/**
 * `parentId`: the group above (same class), or null for a group directly under its class. `autoCode`: the server gives
 * the next code when the group is saved (a preview is not a reservation). `confirmMove`: required to move a group with
 * posted history beneath it.
 */
export interface AccountTypeInput { nameAr: string; nameEn: string; level1Id: number; parentId?: string | null; code?: string | null; autoCode?: boolean; confirmMove?: boolean;
  /** Absent keeps the stored nature (migration 037). */
  nature?: AccountNature | null }
export interface AccountTypeDto {
  id: string; nameAr: string; nameEn: string; level1Id: number; parentId: string | null; code: string | null; depth: number;
  /** The side the group normally carries, as its chart states it (information only, migration 037). */
  nature: AccountNature | null;
  /** The template entry it was installed from ('sa-unified@1:1211'), or null. */
  templateRef: string | null;
  /** The groups above, class side first. */
  path: GroupRef[];
  level1NameEn: string; level1NameAr: string;
  /** Directly beneath: groups and accounts; and accounts anywhere beneath. */
  groupsCount: number; accountsCount: number; totalAccounts: number;
  createdAt: string; updatedAt: string;
}

/** `autoCode`: the server gives the next code (`code` is then ignored). `confirmMove`: required to move an account with journal lines to another group. */
export interface AccountInput { nameAr: string; nameEn: string; code: string; typeId: string; autoCode?: boolean; confirmMove?: boolean;
  /** Absent keeps the stored nature (migration 037). */
  nature?: AccountNature | null }
/** Where a level-3 account is linked in configuration, by kind of record, with counts. */
export type AccountUse = Partial<Record<"taxTypes" | "itemKinds" | "assetKinds" | "warehouses" | "treasuries" | "journalLines" | "vouchers" | "parties" | "organization", number>>;
export interface AccountDto extends AccountInput {
  id: string; typeNameEn: string; typeNameAr: string; level1Id: number; level1NameEn: string; level1NameAr: string;
  /** The groups from the class down to the account's own group. */
  path: GroupRef[];
  usedBy: AccountUse;
  /** All journal lines on the account, debit minus credit (phase 5). */
  balance: Decimal;
  nature: AccountNature | null;
  templateRef: string | null;
  createdAt: string; updatedAt: string;
}
/** Account lists for the dropdowns of other configuration screens. */
export interface AccountOptionsDto { level1: Level1Dto[]; types: GroupOption[]; accounts: (AccountRef & { typeId: string })[]; taxTypes: (Named & { percentage: Decimal })[] }

/** One row of the chart tree. Groups say how much is directly beneath them, so the tree loads one level at a time. */
export interface ChartNode { kind: "group" | "account"; id: string; code: string | null; nameEn: string; nameAr: string; groups: number; accounts: number; nature?: AccountNature | null }
/** The classes with the groups directly under each (rule H-5: the rest loads as it is opened). */
export interface ChartTreeDto { level1: (Level1Dto & { children: ChartNode[] })[] }
export interface ChartChildrenDto { parent: GroupRef; children: ChartNode[] }
/** Search across the whole chart: each hit with the path to it (class id, then groups). At most 200 hits. */
export interface ChartSearchDto { hits: { node: ChartNode; level1Id: number; path: GroupRef[] }[]; more: boolean }

// ---------------------------------------------------------------- chart templates (migration 037, docs/chart-templates.md)

/**
 * The side an account normally carries, as its chart states it: information shown with the account. It never refuses
 * an entry on the other side (contra and settlement accounts, reversals).
 */
export type AccountNature = "debit" | "credit" | "both";
export const ACCOUNT_NATURES: readonly AccountNature[] = ["debit", "credit", "both"];

/** One entry of a template as Delta reads it. The `…Text` fields are the supplied values, word for word. */
export interface TemplateEntry {
  code: string; nameAr: string; nameEn: string; typeText: string; level1Id: number; level: number; levelText: string;
  nature: AccountNature; natureText: string; role: "group" | "posting"; roleText: string; parentCode: string | null;
}
/** A validation result; `detail` lists the codes (or values) that fail it. */
export interface TemplateCheck { key: string; ok: boolean; detail: string[] }
/**
 * Each entry against the organization's chart: `new` (would be created), `installed` (this template created it),
 * `code_in_use` (a group or account of the organization has the code), `code_released` (the code was used and is not
 * given out again), `name_taken` (a group or account of the organization has the name where Delta keeps names unique).
 */
export type TemplateEntryState = "new" | "installed" | "code_in_use" | "code_released" | "name_taken";
/**
 * What an entry meets in the organization's chart. For a group or account: its code and names, and `path`, the groups
 * above it from the top down (code and names), so the screen can show it beside the template entry. A released code
 * has no record (`path` is empty).
 */
export interface TemplateConflict {
  kind: "group" | "account" | "released"; code: string | null; nameEn: string; nameAr: string;
  path: { code: string | null; nameEn: string; nameAr: string }[];
}
export interface ChartTemplateSummary {
  key: string; version: string; titleAr: string; titleEn: string; suppliedOn: string; groups: number; accounts: number;
  installed: { version: string; installedAt: string; installedBy: string; groupsCreated: number; accountsCreated: number } | null;
}
/** Where configuration points at an account: shown for review; nothing is chosen for the owner. */
export interface ConfigReference {
  setting: "customerAccount" | "supplierAccount" | "openingStockAccount" | "stockDifferenceAccount" | "itemKind" | "taxType" | "warehouse" | "treasury" | "treasuryCommission" | "assetKind";
  /** The record that holds the link (an item kind, a tax type…); null for the organization's own settings. */
  record: Named | null;
  /** For records with several accounts (item kinds, asset kinds): which one. */
  field: string | null;
  account: AccountRef | null;
  /** Where it is chosen in Delta. */
  screen: string;
  /** The account was installed from a template. */
  fromTemplate: boolean;
}
export interface ChartTemplatePreviewDto {
  template: ChartTemplateSummary;
  corrections: { code: string; field: "name" | "nature"; from: string; to: string; reason: string }[];
  entries: (TemplateEntry & { state: TemplateEntryState; conflict: TemplateConflict | null })[];
  /** The template's own checks, then the organization's (conflicts, numbering settings). */
  checks: TemplateCheck[];
  /** The organization's own groups and accounts (not installed from a template), and the template rows it has. */
  existing: { groups: number; accounts: number; accountsWithLines: number; journalLines: number; fromTemplate: number; references: ConfigReference[] };
  canInstall: boolean;
  /** Why not, as keys (already_installed, template_invalid, conflicts, numbering, not_owner). */
  blockers: string[];
  /** The organization already has groups or accounts: installing adds the template beside them and needs saying so. */
  needsAlongside: boolean;
}
/** The counts the owner saw, sent back so an install never acts on a template the screen did not show. */
export interface ChartTemplateInstallInput { version: string; groups: number; accounts: number; alongsideExisting?: boolean }
export interface ChartTemplateInstallDto { status: "installed" | "already_installed"; version: string; groupsCreated: number; accountsCreated: number; installedAt: string }
export interface AdoptionMappingInput { accountId: string; templateCode: string }
export interface AdoptionPlanInput { mappings: AdoptionMappingInput[]; note: string }
export interface AdoptionPlanDto {
  version: string;
  mappings: { account: AccountRef & { journalLines: number }; templateCode: string; templateNameEn: string; templateNameAr: string }[];
  /** Existing accounts not yet mapped, with what depends on them. */
  unmapped: (AccountRef & { journalLines: number; references: number })[];
  note: string; updatedAt: string | null; updatedBy: string | null;
  /** Replacing the organization's chart is never done by Delta; the reasons it is blocked for this organization. */
  replacement: { offered: false; blockedBy: { accountsWithLines: number; journalLines: number; references: number } };
}

/**
 * Account numbering (rules H-2 to H-4). The settings and, for deciding them, what the existing codes look like.
 */
export interface AccountNumberingInput {
  autoSuggest: boolean; allowManual: boolean; childSegmentLength: number | null; maxLength: number; reuseReleased: boolean; requireParentPrefix: boolean;
}
export interface AccountNumberingDto extends AccountNumberingInput {
  updatedAt: string | null;
  observed: { accountCodeLengths: { length: number; count: number }[]; groupsWithCode: number; groupsWithoutCode: number; released: number; leadingZeros: number };
}
export type NextCodeReason = "after_siblings" | "first_child" | "no_segment_rule" | "parent_has_no_code" | "mixed_lengths" | "exhausted" | "suggestions_off";
/** The code the server would give now (a preview: nothing is held). `code` is null when it cannot suggest one, with the reason. */
export interface NextCodeDto { code: string | null; reason: NextCodeReason; length: number | null; prefix: string | null; siblings: number; alternatives: string[] }

export interface CostCenterDto extends NamedInput { id: string; journalLinesCount: number; createdAt: string; updatedAt: string }

export interface TaxTypeInput { nameAr: string; nameEn: string; percentage: DecimalInput; accountId: string }
export interface TaxTypeDto extends Omit<TaxTypeInput, "percentage"> { id: string; percentage: Decimal; account: AccountRef; itemsCount: number; treasuriesCount: number; createdAt: string; updatedAt: string }

export const ITEM_KIND_ACCOUNTS = ["salesAccountId", "salesRefundAccountId", "salesCostAccountId", "inventoryAccountId", "corruptedAccountId", "discountAccountId"] as const;
export type ItemKindAccount = (typeof ITEM_KIND_ACCOUNTS)[number];
export type ItemKindInput = NamedInput & { hasQty: boolean } & Record<ItemKindAccount, string>;
export type ItemKindDto = ItemKindInput & { id: string; accounts: Record<ItemKindAccount, AccountRef>; itemsCount: number; createdAt: string; updatedAt: string };

// ---------------------------------------------------------------- Phase 5: journal entries and the general ledger
// Amounts are exact decimal text. A balance is debit minus credit: positive is a debit balance, negative a credit one.

export interface PartyRef { id: string; kind: PartyKind; name: string }
export interface JournalLineInput { accountId: string; partyId?: string | null; partnerId?: string | null; costCenterId?: string | null; debit?: DecimalInput; credit?: DecimalInput; note?: string }
/** entryAt is the local date and time "YYYY-MM-DDTHH:mm". */
/** A draft's fields. With `post: true` the draft is posted in the same request (rule L-9). */
export interface JournalEntryInput { entryAt: string; statement: string; lines: JournalLineInput[]; post?: boolean }
export interface JournalLineDto { lineNo: number; account: AccountRef; party: PartyRef | null; partner: PartnerRef | null; costCenter: Named | null; debit: Decimal; credit: Decimal; note: string }
export interface JournalEntryListDto {
  id: string;
  /** A draft is editable and outside the ledger; a posted entry is locked (rule L-9). */
  status: "draft" | "posted";
  /** Given when the entry is posted (rule L-4); null for a draft and for an entry migrated from the legacy system. */
  number: string | null;
  entryAt: string; statement: string; sourceType: "manual" | "reversal" | "voucher" | "stock" | "invoice" | "asset_invoice" | "asset_depreciation" | "legacy";
  /** A migrated entry's legacy number, verbatim (D-2), and how it departs from Delta's rules as stored (M-5b). */
  legacyNumber: string | null; legacyFlags: ("header_only" | "unbalanced" | "zero_records")[]; total: Decimal; linesCount: number;
  createdByName: string | null; createdAt: string; updatedAt: string; postedAt: string | null; postedByName: string | null;
  /** A draft finished under a manual posting policy by someone who may not post it: waiting for posting (migration 034). */
  submittedAt: string | null; submittedByName: string | null;
  /** For a reversal (rule L-9): the entry it reverses. */
  reverses: { id: string; number: string } | null;
  /** For a reversed entry: its reversal. */
  reversedBy: { id: string; number: string } | null;
  /** The voucher that made the entry (source type "voucher"), or whose reversal it is. */
  voucher: VoucherRef | null;
  /** The stock document that made the entry (source type "stock"), or whose reversal it is. */
  stockDocument: StockDocRef | null;
  /** The invoice that made the entry (source type "invoice"), or whose reversal it is. */
  invoice: { id: string; kind: string; number: string | null } | null;
  /** The asset purchase or the depreciation period that made the entry (or whose reversal it is): `id` is the purchase's or the asset's. */
  asset: { source: "asset_invoice" | "asset_depreciation"; id: string; label: string } | null;
}
/** Reverse a posted entry (rule L-9): a new entry on this date with every line's debit and credit swapped. */
export interface JournalReverseInput { entryAt: string; statement?: string }
/** A legacy document kept as stored beside a migrated entry (M-5b): its legacy table, kind and number verbatim. */
export interface LegacyDocumentRef { id: string; table: "Invoice" | "Money" | "Transfer" | "PosDeport" | "AssetInvoice" | "AssetDepreciation" | "Quotation"; kind: string | null; number: string | null; documentAt: string | null; linesCount: number }
export interface JournalEntryDto extends JournalEntryListDto {
  lines: JournalLineDto[];
  /** For a migrated entry: the legacy documents that name it, as stored. Empty otherwise. */
  legacyDocuments: LegacyDocumentRef[];
}
// Legacy history (migration 020, M-5b): the documents the migration kept as stored, read only. They are history, not
// Delta documents: never posted, recalculated or counted in Delta's sales, purchase, VAT or stock figures. Every value
// is the legacy row's own text; Delta adds only the names of the Delta rows its references resolve to.
export type LegacyTable = LegacyDocumentRef["table"];
export const LEGACY_TABLES: readonly LegacyTable[] = ["Invoice", "Money", "Transfer", "PosDeport", "AssetInvoice", "AssetDepreciation", "Quotation"];
/** The stored amount fields shown in the list, per legacy table, verbatim. */
export const LEGACY_LIST_FIELDS: Record<LegacyTable, readonly string[]> = {
  Invoice: ["SubTotal", "Discount", "Tax", "PaidUp", "PaidLater"], Money: ["AmountIn", "AmountOut"], Transfer: ["Amount"], PosDeport: ["Total"],
  AssetInvoice: ["SubTotal", "Tax", "PaidUp", "PaidLater"], AssetDepreciation: ["Amount"], Quotation: ["SubTotal", "Discount", "Tax"]
};
export interface LegacyNamed { id: string; nameEn: string; nameAr: string }
export interface LegacyHistoryRowDto extends Omit<LegacyDocumentRef, "linesCount"> {
  /** The branch the document belongs to: its own (Money), or its warehouse's or treasury's. */
  branch: LegacyNamed | null;
  party: { id: string; kind: string; name: string } | null;
  warehouse: LegacyNamed | null;
  /** LEGACY_LIST_FIELDS of its table, as stored (a field the row does not have is left out). */
  fields: Record<string, string | null>;
  entriesCount: number;
  linesCount: number;
}
export interface LegacyHistoryListDto {
  rows: LegacyHistoryRowDto[];
  total: number; page: number; pageSize: number;
  /** Every legacy document of the organization, and how many of them the user's branch, warehouse and treasury scope leaves out. */
  inOrganization: number; hiddenByScope: number;
  /** The tables and kinds held, with counts in scope, for the filters. */
  kinds: { table: LegacyTable; kind: string | null; count: number }[];
  branches: LegacyNamed[];
}
export interface LegacyHistoryQuery { q?: string; from?: string; to?: string; branchId?: string; table?: string; kind?: string; page?: string | number; pageSize?: string | number }
export interface LegacyHistoryDocDto extends LegacyHistoryRowDto {
  legacyId: string;
  /** The legacy row exactly as extracted, every value as text. */
  data: Record<string, unknown>;
  /** The Delta rows its references resolve to (null where the reference is empty or not held in Delta). */
  refs: {
    toWarehouse: LegacyNamed | null; treasury: LegacyNamed | null; toTreasury: LegacyNamed | null; costCenter: LegacyNamed | null; taxType: LegacyNamed | null;
    account: (LegacyNamed & { code: string | null }) | null; secondAccount: (LegacyNamed & { code: string | null }) | null;
  };
  lines: { lineNo: number; table: string; data: Record<string, unknown>; item: { id: string; code: string | null; nameEn: string; nameAr: string; unitEn: string; unitAr: string } | null;
    warehouse: LegacyNamed | null; taxType: LegacyNamed | null }[];
  /** The migrated journal entries the legacy rows link to the document (JournalEntry.BelongType and BelongId). */
  entries: { id: string; legacyNumber: string | null; entryAt: string; statement: string; total: Decimal; flags: string[] }[];
  /** Settlements and refunds between documents, as stored, in both directions; nothing is allocated in Delta from them. */
  links: { kind: "settlement" | "refund_of"; direction: "from" | "to"; amount: Decimal | null; legacyId: string | null; document: LegacyDocumentRef | null }[];
}

/** `total` is of the posted entries listed; `drafts` counts the drafts listed. */
export interface JournalListDto { entries: JournalEntryListDto[]; total: Decimal; drafts: number; awaiting: number }
/** Lists for the journal line editor and the report filters. */
export interface LedgerOptionsDto {
  level1: Level1Dto[];
  types: GroupOption[];
  accounts: (AccountRef & { typeId: string; level1Id: number })[];
  parties: PartyRef[];
  costCenters: Named[];
  /** Partners (rule G-2): a line on a partner's account may name the partner. */
  partners: PartnerRef[];
  /** Treasuries inside the caller's scope. */
  treasuries: (Named & { kind: "cash" | "bank"; accountId: string | null })[];
}

export interface PeriodQuery { from?: string; to?: string }
export interface AccountMovementRow {
  account: AccountRef & { typeId: string; level1Id: number };
  /** Before the period. */
  openingDebit: Decimal; openingCredit: Decimal;
  /** Inside the period. */
  debit: Decimal; credit: Decimal;
  /** Opening plus period, debit minus credit. */
  closing: Decimal;
}
/** A group's subtotal: every account beneath it counted once (rule H-6). */
export interface GroupMovementRow {
  group: GroupRef & { parentId: string | null; level1Id: number; depth: number };
  openingDebit: Decimal; openingCredit: Decimal; debit: Decimal; credit: Decimal; closing: Decimal;
}
export interface AccountMovementsDto {
  from: string | null; to: string | null; rows: AccountMovementRow[];
  /** Subtotals of the groups that have rows beneath them, in tree order (parents before children). */
  groups: GroupMovementRow[];
  totals: { openingDebit: Decimal; openingCredit: Decimal; debit: Decimal; credit: Decimal; closingDebit: Decimal; closingCredit: Decimal };
}
/** `source`: the document that made the entry, for drill-down (null for manual and migrated entries). */
export interface StatementRowDto { entryId: string; number: string; entryAt: string; statement: string; note: string; account: AccountRef; source: SourceRef | null; branch: Named | null; debit: Decimal; credit: Decimal; balance: Decimal }
/**
 * `branch`: the branch filter, when one was given; the statement then holds only entries of that branch's documents,
 * and `unbranched` counts the entries in the period left out because they belong to no branch (manual, migrated).
 */
export interface StatementDto {
  from: string | null; to: string | null; branch: Named | null; unbranched: number; opening: Decimal; rows: StatementRowDto[]; debit: Decimal; credit: Decimal; closing: Decimal;
  /** The ledger balance at the end date, summed on its own (not from the rows), and closing − ledger: zero when the statement reconciles. */
  ledger: Decimal; difference: Decimal;
}
/**
 * One customer or supplier: `opening` is debit minus credit of everything dated before `from`; `debit` and `credit`
 * are the period's movements; `closing` = opening + debit − credit, the same as the party statement's closing
 * balance for the same dates.
 */
export interface PartyBalanceRow { party: PartyRef; phone: string; opening: Decimal; debit: Decimal; credit: Decimal; closing: Decimal }
export interface PartyBalancesDto { from: string | null; to: string | null; rows: PartyBalanceRow[]; opening: Decimal; debit: Decimal; credit: Decimal; closing: Decimal }
/**
 * One row of the Treasury report. Several treasuries can share one account (migrated businesses often do), so a row is:
 * - basis "account": the treasury is its account's only treasury, and the row is the account's balance;
 * - basis "legacy_treasury": the account is shared; the row holds only the migrated ledger lines whose legacy record
 *   names this treasury (JournalEntryRecords.JournalId), as stored;
 * - basis "shared_remainder" (treasury null): the rest of a shared account's balance, which no single treasury can be
 *   shown to own (lines naming no treasury, or another one; Delta's own postings). Shown once, never allocated.
 * Every account's balance is therefore counted exactly once in the totals.
 */
export interface TreasuryBalanceRow {
  treasury: (Named & { kind: "cash" | "bank" }) | null; account: AccountRef | null; debit: Decimal; credit: Decimal; balance: Decimal;
  basis: "account" | "legacy_treasury" | "shared_remainder";
  /** How many treasuries are linked to this row's account (1 when it is not shared). */
  sharedBy: number;
}
export interface TreasuryBalancesDto { rows: TreasuryBalanceRow[]; debit: Decimal; credit: Decimal; balance: Decimal; /** Accounts linked to more than one treasury, among the rows. */ sharedAccounts: number }

// ---------------------------------------------------------------- Phases 6a and 6b: treasury vouchers
// Expenses, deposits, withdrawals and cash transfers (rules V-1 to V-4, docs/proposed-financial-rules.md, section 3),
// collections and payments (rules C-1 to C-5, section 4). All provisional, for synthetic staging and accountant
// testing only; the server refuses to post under them in production.

export type VoucherKind = "expense" | "deposit" | "withdraw" | "cash_transfer" | "collect" | "payment" | "opening_balance";
export const VOUCHER_KINDS: readonly VoucherKind[] = ["expense", "deposit", "withdraw", "cash_transfer", "collect", "payment", "opening_balance"];
/** Display prefixes of the approved prototype (rules V-3, C-3): EX-1, DP-1, WD-1, CT-1, CL-1, PY-1; opening balances OB-1 (rule O-1). The stored number has none. */
export const VOUCHER_PREFIX: Record<VoucherKind, string> = { expense: "EX-", deposit: "DP-", withdraw: "WD-", cash_transfer: "CT-", collect: "CL-", payment: "PY-", opening_balance: "OB-" };
/** The party a collection or payment is with. An opening balance (rule O-1) is with a client or a supplier. */
export const VOUCHER_PARTY: Partial<Record<VoucherKind, PartyKind>> = { collect: "customer", payment: "supplier" };
export const voucherCode = (kind: VoucherKind, number: string | null) => (number ? VOUCHER_PREFIX[kind] + number : null);
export interface VoucherRef { id: string; kind: VoucherKind; number: string | null }

export interface VoucherTreasuryInput { treasuryId: string; amount: DecimalInput }
/**
 * A voucher's fields. Expense, deposit and withdrawal: branch, account, treasuries with amounts (and a cost center
 * for an expense). Cash transfer: source and target treasuries, amount, optional bank deposit. `voucherAt` is the
 * local date and time "YYYY-MM-DDTHH:mm". With `post: true` the draft is posted in the same request (rule V-2).
 */
export interface VoucherInput {
  voucherAt: string; note?: string; post?: boolean;
  /** Collection: the customer; payment: the supplier. Their account is the party's own (rule C-1), not chosen here. */
  partyId?: string | null;
  /** Deposit or withdrawal (rule G-3): the partner whose account `accountId` is. */
  partnerId?: string | null;
  branchId?: string | null; accountId?: string | null; costCenterId?: string | null; treasuries?: VoucherTreasuryInput[];
  fromTreasuryId?: string | null; toTreasuryId?: string | null; amount?: DecimalInput; deposited?: boolean; depositDate?: string | null; depositNumber?: string;
  /** Opening balance (rule O-2): the side of the party's account it is on. */
  side?: "debit" | "credit";
  /**
   * Expense only (migration 038): one of the organization's tax types, or null/absent for no tax. With a tax, `amount`
   * is the amount entered, `taxMode` says whether it includes the tax, and `taxRecoverable` whether the tax is input
   * tax posted to the tax type's account or a cost added to the expense; the treasuries must then add up to the total
   * including tax (expenseTaxCalc).
   */
  taxTypeId?: string | null; taxMode?: ExpenseTaxMode; taxRecoverable?: boolean;
  /**
   * Expense only (migration 045): the expense type. The server takes the account from the type. Its default cost center
   * is used only when `costCenterId` is absent from the request (API clients); an explicit null means no cost center
   * (30 Oct 2026, 22:13: a blank choice never inherits the default silently). Choosing `accountId` directly is advanced: only the business owner or a
   * user granted journalEntryAdd may send it (expense_account_not_allowed otherwise; the administrator flag alone is
   * not enough). With a type, `accountId` must be absent.
   */
  expenseTypeId?: string | null;
  /**
   * Expense only (migration 054): the expense's own name, such as "Office rent, October"; separate from the note.
   * Optional (empty or absent: none).
   */
  title?: string | null;
}
export type ExpenseTaxMode = "exclusive" | "inclusive";
/** The tax of an expense as it was saved: the tax type's name, rate and account then, and the calculation. */
export interface ExpenseTaxDto {
  taxType: Named; rate: Decimal; mode: ExpenseTaxMode; recoverable: boolean; account: AccountRef;
  /** Migration 056: "chosen" by a user who may choose accounts, "default" the business default; null: zero rate, or saved before 056. */
  source?: "chosen" | "default" | null;
  entered: Decimal; beforeTax: Decimal; tax: Decimal; total: Decimal;
}
/**
 * Tax on an expense (migration 038), in exact decimals, rounding to two decimals half away from zero as invoices do
 * (rules B-2 and R-2): excluding tax, tax = amount × rate ÷ 100 and the total is amount + tax; including tax, the
 * amount before tax is amount × 100 ÷ (100 + rate) and the tax is the rest. 100.00 at 15 % excluding: 15.00, 115.00;
 * 115.00 at 15 % including: 100.00 and 15.00; 10.01 at 15 % including: 8.70 and 1.31.
 */
export function expenseTaxCalc(amount: string, rate: string, mode: ExpenseTaxMode): { beforeTax: string; tax: string; total: string } {
  const D = Exact.dec, r2 = (x: Exact.Dec) => Exact.roundHalfAway(x, 2), f = (x: Exact.Dec) => Exact.fmt(x, 2);
  const a = r2(D(amount)), R = D(rate), HUNDRED = D("100");
  if (mode === "exclusive") { const tax = Exact.percentOf(a, R); return { beforeTax: f(a), tax: f(tax), total: f(Exact.add(a, tax)) }; }
  const before = Exact.div(Exact.mul(a, HUNDRED), Exact.add(HUNDRED, R), 2);
  return { beforeTax: f(before), tax: f(Exact.sub(a, before)), total: f(a) };
}
export interface VoucherReverseInput { reversedAt: string; statement?: string }
export type TreasuryRef = Named & { kind: "cash" | "bank" };
export interface VoucherListDto {
  id: string; kind: VoucherKind; status: "draft" | "posted";
  /** Given when posted (rule V-3); `code` adds the prefix. Null for a draft. */
  number: string | null; code: string | null;
  voucherAt: string; branch: Named | null; account: AccountRef | null; costCenter: Named | null;
  /** Expense only (migration 054): its own name; null when none was given. */
  title?: string | null;
  party: PartyRef | null;
  /** Deposit or withdrawal: the partner (rule G-3). */
  partner: PartnerRef | null;
  /** `commission` and `commissionTax` (rule C-4, collections into a bank treasury with a commission rate); `net` is what reaches the treasury. */
  treasuries: { treasury: TreasuryRef; amount: Decimal; commission: Decimal; commissionTax: Decimal; net: Decimal }[];
  fromTreasury: TreasuryRef | null; toTreasury: TreasuryRef | null;
  deposited: boolean; depositDate: string | null; depositNumber: string;
  note: string; total: Decimal;
  createdByName: string | null; createdAt: string; updatedAt: string; postedAt: string | null; postedByName: string | null;
  /** A draft finished under a manual posting policy by someone who may not post it: waiting for posting (migration 034). */
  submittedAt: string | null; submittedByName: string | null;
  /** The posted journal entry, and the entry that reversed it. */
  journalEntry: { id: string; number: string } | null; reversal: { id: string; number: string } | null; reversedAt: string | null;
  legacyNumber: string | null;
  /** Opening balance: the side of the party's account (rule O-2); null for the other kinds. */
  side: "debit" | "credit" | null;
  /** Collection or payment: the part settled against invoices (rule A-2). "0.00" for the other kinds. */
  settled: Decimal;
  /** Expense: its tax as saved (migration 038); null without tax and for the other kinds. */
  tax: ExpenseTaxDto | null;
  /** Expense: its expense type with the names it had when saved (migration 045); null when the account was chosen directly. */
  expenseType: Named | null;
}
/**
 * `warnings` (rule V-4), for a draft: each treasury the voucher pays out of whose current balance (its linked
 * account, posted entries) would fall below zero. Posting is still allowed.
 */
export interface VoucherDto extends VoucherListDto {
  warnings: { treasury: TreasuryRef; balance: Decimal; after: Decimal }[];
  /**
   * Rule C-5, for a draft collection or payment: the party's current balance (posted lines tagged with it, debit minus
   * credit) and its balance after this voucher. A result on the other side is an advance; posting is allowed.
   */
  partyBalance: { balance: Decimal; after: Decimal; advance: boolean } | null;
}
/** `total` is of the posted vouchers listed; `drafts` counts the drafts listed. */
export interface VouchersDto { vouchers: VoucherListDto[]; total: Decimal; drafts: number; awaiting: number }
export interface VoucherOptionsDto {
  /** Branches inside the caller's scope. */
  branches: Named[];
  accounts: (AccountRef & { level1Id: number })[];
  /** Accounts linked to a treasury: not offered for deposits and withdrawals (rule V-4). */
  treasuryAccountIds: string[];
  costCenters: Named[];
  /**
   * Treasuries inside the caller's scope, with the current balance of the linked account (null without one), and
   * the commission rate and commission tax rate that apply to collections into it (rule C-4; "0.00" when none).
   */
  treasuries: (TreasuryRef & { branchId: string; accountId: string | null; balance: Decimal | null; commission: Decimal; commissionTaxRate: Decimal })[];
  /** Clients and suppliers, with their account (null: cannot be used until one is set) and current balance. */
  parties: (PartyRef & { accountId: string | null; balance: Decimal })[];
  /** Partners for deposits and withdrawals (rule G-3): the voucher's account is the partner's. */
  partners: PartnerRef[];
  /** The organization's tax types for an expense's tax (migration 038), with their rate and account. */
  taxTypes: (Named & { rate: Decimal; account: AccountRef })[];
  /**
   * Active expense types (migration 045), with the default cost center the form preselects. `account` is given only
   * to a user who may choose the account (`canChooseExpenseAccount`); everyone else picks the type and the server
   * resolves the account.
   */
  expenseTypes: (Named & { account: AccountRef | null; costCenterId: string | null })[];
  /** The business owner, or a user granted journalEntryAdd: may see and choose an expense's account directly. */
  canChooseExpenseAccount: boolean;
  /** Migration 056: the business default for an expense's input tax (null: not set yet). */
  expenseTaxDefault: boolean | null;
}

/** An expense type (Settings › Expense types, migration 045): its names, posting account and default cost center. */
export interface ExpenseTypeDto {
  id: string; nameEn: string; nameAr: string; account: AccountRef; costCenter: Named | null; active: boolean;
  /** Expenses that name the type (any status); a type in use is deactivated, never deleted. */
  expensesCount: number;
  createdAt: string; updatedAt: string;
}
export interface ExpenseTypeInput { nameEn: string; nameAr: string; accountId: string; costCenterId?: string | null; active: boolean }
/** Accounts an expense type may post to (the chart classifies them under Expenses) and cost centers. */
export interface ExpenseTypeOptionsDto {
  accounts: AccountRef[]; costCenters: Named[];
  /** The business owner, or a user granted journalEntryAdd: may add and change types (they link accounts). */
  canManage: boolean;
}

/**
 * Rule C-4 (P-9), provisional: card commission on a collection into a bank treasury with a commission rate.
 * commission = amount × rate ÷ 100 and tax = commission × tax rate ÷ 100, each rounded to two decimals half away from
 * zero; the treasury receives amount − commission − tax. Example: 1,500.00 at 1.75 % with a 12.50 % tax:
 * commission 26.25, tax 3.28 (3.28125), net 1,470.47.
 */
export function cardCommission(amount: string, rate: string, taxRate: string): { commission: string; commissionTax: string; net: string } {
  const a = Exact.dec(amount), c = Exact.percentOf(a, Exact.dec(rate)), tax = Exact.percentOf(c, Exact.dec(taxRate));
  return { commission: Exact.fmt(c, 2), commissionTax: Exact.fmt(tax, 2), net: Exact.fmt(Exact.sub(Exact.sub(a, c), tax), 2) };
}

// ---------------------------------------------------------------- Phase 8: stock core
// Beginning inventory, damaged stock (legacy Corrupted), warehouse transfers, inventory counts and compromises, the
// stock ledger and stock on hand, under rules S-1 to S-7 (docs/proposed-financial-rules.md, section 5). Provisional,
// for synthetic staging and accountant testing only; the server refuses to post under them in production.

export type StockKind = "beginning_inventory" | "corrupted" | "stock_transfer" | "inventory" | "compromise";
export const STOCK_KINDS: readonly StockKind[] = ["beginning_inventory", "corrupted", "stock_transfer", "inventory", "compromise"];
/** Display prefixes of the approved prototype: BI-1, CR-1, TR-1, IV-1, CP-1. The stored number has none. */
export const STOCK_PREFIX: Record<StockKind, string> = { beginning_inventory: "BI-", corrupted: "CR-", stock_transfer: "TR-", inventory: "IV-", compromise: "CP-" };
export const stockCode = (kind: StockKind, number: string | null) => (number ? STOCK_PREFIX[kind] + number : null);
/** Counts enter the counted quantity; the other kinds enter a quantity to move. */
export const isCountKind = (k: StockKind) => k === "inventory" || k === "compromise";
/** Permission keys of each kind (audited route table), mapped as rule L-3 maps the journal keys. */
export const STOCK_KEYS: Record<StockKind, { view: PermissionKey; add: PermissionKey; update: PermissionKey; del: PermissionKey }> = {
  beginning_inventory: { view: "invoiceBeginningInventories", add: "invoiceBeginningInventoryAdd", update: "invoiceBeginningInventoryUpdate", del: "invoiceBeginningInventoryDelete" },
  corrupted: { view: "invoiceCorrupteds", add: "invoiceCorruptedAdd", update: "invoiceCorruptedUpdate", del: "invoiceCorruptedDelete" },
  stock_transfer: { view: "invoiceTransfers", add: "invoiceTransferAdd", update: "invoiceTransferUpdate", del: "invoiceTransferDelete" },
  inventory: { view: "invoiceInventories", add: "invoiceInventoryAdd", update: "invoiceInventoryUpdate", del: "invoiceInventoryDelete" },
  compromise: { view: "invoiceCompromises", add: "invoiceCompromiseAdd", update: "invoiceCompromiseUpdate", del: "invoiceCompromiseDelete" }
};
export interface StockDocRef { id: string; kind: StockKind; number: string | null }

/**
 * A line: an item in one of its units (`itemUnitId`). Beginning inventory, damaged stock and transfers: `qty` (and
 * `price`, the unit price, for beginning inventory). Counts: `actualQty`, the counted quantity, and optionally `price`,
 * which values a surplus (rule S-4; defaults to the current average).
 */
export interface StockLineInput { itemId: string; itemUnitId: string; qty?: DecimalInput | null; actualQty?: DecimalInput | null; price?: DecimalInput | null }
/** `docAt` is the local date and time "YYYY-MM-DDTHH:mm". With `post: true` the draft is posted in the same request. */
export interface StockDocInput { docAt: string; warehouseId: string; toWarehouseId?: string | null; note?: string; lines: StockLineInput[]; post?: boolean }
export interface StockDocReverseInput { reversedAt: string; statement?: string }
export interface StockLineDto {
  lineNo: number;
  item: { id: string; code: string; nameEn: string; nameAr: string };
  unit: { id: string; nameEn: string; nameAr: string; pieces: Decimal };
  qty: Decimal | null; actualQty: Decimal | null;
  /** Price per entered unit: the entered price for beginning inventory and count surpluses, the average cost otherwise. */
  price: Decimal;
  /** The price as entered on the draft (null: the average applies). */
  enteredPrice: Decimal | null;
  /** Counts: the system quantity in the entered unit (found at posting; the current one for a draft) and the difference. */
  systemQty: Decimal | null; difference: Decimal | null;
  /** The line's value: fixed at posting; for a draft, an estimate at the current averages. Signed for counts. */
  value: Decimal;
  /** Stock of the item in the document's warehouse now, in the entered unit (drafts only). */
  available: Decimal | null;
}
export interface StockDocListDto {
  id: string; kind: StockKind; status: "draft" | "posted";
  number: string | null; code: string | null;
  docAt: string; warehouse: Named; toWarehouse: Named | null; branch: Named;
  note: string; total: Decimal; linesCount: number;
  createdByName: string | null; createdAt: string; updatedAt: string; postedAt: string | null; postedByName: string | null;
  /** The journal entry (null when the document moved no value) and the entry that reversed it. */
  journalEntry: { id: string; number: string } | null; reversal: { id: string; number: string } | null;
  reversedAt: string | null; reversalAt: string | null;
  legacyNumber: string | null;
  /** The migration cutover opening (CV-2): no journal entry, never reversed; its lines' prices are not stated. */
  cutover: boolean;
}
/** `warnings` (rule S-6), for a draft: lines that take more than the warehouse holds; posting refuses them. */
export interface StockDocDto extends StockDocListDto { lines: StockLineDto[]; warnings: { lineNo: number; available: Decimal; qty: Decimal }[] }
/** `total` is of the posted documents listed; `drafts` counts the drafts listed. */
export interface StockDocsDto { documents: StockDocListDto[]; total: Decimal; drafts: number }

export interface StockOptionsDto {
  /** Warehouses inside the caller's scope. */
  warehouses: (Named & { branchId: string; accountId: string | null })[];
  /** Items whose kind holds stock, with their units (the first is the base unit) and barcodes. */
  items: { id: string; code: string; nameEn: string; nameAr: string; categoryId: string; units: { id: string; nameEn: string; nameAr: string; pieces: Decimal; barcodes: string[] }[] }[];
  categories: Named[];
  /** Quantity (base units) and value on hand of each item in each warehouse in scope. */
  positions: { warehouseId: string; itemId: string; qty: Decimal; value: Decimal }[];
}
/** Stock on hand: one row per item and warehouse with stock, in base units. `averageCost` is value ÷ qty, rounded to four decimals for display. */
export interface StockRowDto {
  warehouse: Named; item: { id: string; code: string; nameEn: string; nameAr: string; minQty: Decimal }; category: Named; baseUnit: Named;
  qty: Decimal; value: Decimal; averageCost: Decimal;
}
export interface StockOnHandDto { rows: StockRowDto[]; value: Decimal }
/** `document` is the stock document or, when `invoice` is true, the invoice that moved the stock. */
export interface StockMoveDto { seq: string; movedAt: string; document: { id: string; kind: string; number: string | null; code: string | null; invoice: boolean }; warehouse: Named; qty: Decimal; value: Decimal; reversal: boolean }
/** An item's stock by warehouse and its latest movements, within the caller's scope. */
export interface ItemStockDto { rows: StockRowDto[]; moves: StockMoveDto[] }

// ---------------------------------------------------------------- Phase 7a: purchases and purchase refunds
// Purchase invoices and purchase refunds under the provisional rules B-1 to B-7 (docs/proposed-financial-rules.md,
// section 6), for synthetic staging and accountant testing only; the server refuses to post under them in
// production. The calculation (rule B-2, proposal P-1) is here so the screen and the server compute the same cents.

export type InvoiceKind = "purchase" | "purchase_refund" | "sale" | "sale_refund" | "quotation";
export const INVOICE_KINDS: readonly InvoiceKind[] = ["purchase", "purchase_refund", "sale", "sale_refund", "quotation"];
/** Display prefixes of the approved prototype (erp-core.js PREFIX): PI-1, PR-1, SI-1, SR-1, QT-1. The stored number has none. */
export const INVOICE_PREFIX: Record<InvoiceKind, string> = { purchase: "PI-", purchase_refund: "PR-", sale: "SI-", sale_refund: "SR-", quotation: "QT-" };
export const invoiceCode = (kind: InvoiceKind, number: string | null) => (number ? INVOICE_PREFIX[kind] + number : null);
/**
 * Permission keys of each kind (audited route table), mapped as rule L-3 maps the journal keys. Quotations have no
 * keys of their own in the audited table (/invoices/Quotation needs "Invoice Sales"): they use the sales keys.
 */
export const INVOICE_KEYS: Record<InvoiceKind, { view: PermissionKey; add: PermissionKey; update: PermissionKey; del: PermissionKey }> = {
  purchase: { view: "invoicePurchases", add: "invoicePurchaseAdd", update: "invoicePurchaseUpdate", del: "invoicePurchaseDelete" },
  purchase_refund: { view: "invoicePurchaseRefunds", add: "invoicePurchaseRefundAdd", update: "invoicePurchaseRefundUpdate", del: "invoicePurchaseRefundDelete" },
  sale: { view: "invoiceSales", add: "invoiceSaleAdd", update: "invoiceSaleUpdate", del: "invoiceSaleDelete" },
  sale_refund: { view: "invoiceSaleRefunds", add: "invoiceSaleRefundAdd", update: "invoiceSaleRefundUpdate", del: "invoiceSaleRefundDelete" },
  quotation: { view: "invoiceSales", add: "invoiceSaleAdd", update: "invoiceSaleUpdate", del: "invoiceSaleDelete" }
};
/** The refund kind of each kind that can be refunded. */
export const REFUND_KIND: Partial<Record<InvoiceKind, InvoiceKind>> = { purchase: "purchase_refund", sale: "sale_refund" };
/** The party side of each kind: purchases are with suppliers, sales and quotations with customers. */
export const invoiceSide = (k: InvoiceKind): "supplier" | "customer" => (k === "purchase" || k === "purchase_refund" ? "supplier" : "customer");
export interface InvoiceRef { id: string; kind: InvoiceKind; number: string | null }

/**
 * A purchase line: an item in one of its units, the quantity, the unit price before VAT, a line discount given as a
 * percentage or as an amount (one of them, or neither), and the tax type.
 */
export interface InvoiceLineInput { itemId: string; itemUnitId: string; qty: DecimalInput; price: DecimalInput; discountPct?: DecimalInput | null; discountAmount?: DecimalInput | null; taxTypeId: string }
export interface InvoicePaymentInput { treasuryId: string; amount: DecimalInput }
/** `invoiceAt` is "YYYY-MM-DDTHH:mm". The invoice discount is a percentage or an amount (one of them, or neither). */
export interface InvoiceInput {
  /** null only for a walk-in POS sale (W-9), which the register makes. */
  invoiceAt: string; warehouseId: string; partyId: string | null; reference?: string; note?: string;
  discountPct?: DecimalInput | null; discountAmount?: DecimalInput | null;
  lines: InvoiceLineInput[]; payments: InvoicePaymentInput[];
  /**
   * `complete`: save and complete (the sale or purchase happened: number, stock, payments; not in the ledger).
   * `post`: save, complete and post to the accounts at once (the business owner only).
   * `confirmAmend`: changing a completed invoice needs it (the first try answers amend_confirm_required with what changes).
   * `confirmTobaccoTotal`: when the minimum tobacco fee is added on top of fee-inclusive prices (case b), the revised
   * total the user saw and accepted; the first try answers tobacco_total_confirm_required (details.tobaccoTotal).
   */
  post?: boolean; complete?: boolean; confirmAmend?: boolean; confirmTobaccoTotal?: string | null;
}
/** A total raised by the minimum tobacco fee, returned with tobacco_total_confirm_required (details.tobaccoTotal). */
export interface TobaccoTotalRaise { entered: Decimal; revised: Decimal; raised: Decimal; lines: { line: number; entered: Decimal; revised: Decimal; fee: Decimal }[] }
/** A refund of a posted invoice: quantities per line of the original (by its line number) and the money returned. */
export interface RefundInput { invoiceAt: string; note?: string; lines: { lineNo: number; qty: DecimalInput }[]; payments: InvoicePaymentInput[]; post?: boolean; complete?: boolean; confirmAmend?: boolean }
export interface InvoiceReverseInput { reversedAt: string; statement?: string }

export interface InvoiceLineDto {
  lineNo: number;
  item: { id: string; code: string; nameEn: string; nameAr: string; hasQty: boolean };
  unit: { id: string; nameEn: string; nameAr: string; pieces: Decimal };
  taxType: Named & { rate: Decimal };
  qty: Decimal; price: Decimal; discountPct: Decimal | null;
  /** Sales: the price includes VAT (the item's setting when the line was saved, rule R-2). */
  priceIncludesTax: boolean;
  /** Calculated (rule B-2): gross = qty × price; line discount; share of the invoice discount; before tax; VAT; total. */
  gross: Decimal; lineDiscount: Decimal; invoiceDiscount: Decimal; taxable: Decimal; tax: Decimal; total: Decimal;
  /** Refunds: the line of the original invoice. Invoices: quantity already refunded by posted refunds. */
  refundOfLine: number | null; refundedQty: Decimal;
  /** Sales: the list price the line was checked against and who set a different price (null: the list price, or saved before migration 039). */
  listPrice: Decimal | null; priceSetByName: string | null;
  /** Migration 049: the tobacco municipality fee this line charged (0.00 without one), separate from VAT, and whether the minimum set it. */
  tobaccoFee: Decimal; tobaccoMinimumApplied: boolean;
}
/** commission and commissionTax: rule C-4 on a sale's payment into a treasury with a commission rate ("0.00" otherwise). */
export interface InvoicePaymentDto { treasury: Named & { kind: string }; amount: Decimal; commission: Decimal; commissionTax: Decimal }
/**
 * draft: unfinished (moves nothing). completed: the sale or purchase happened (number, stock, payments, shift) and
 * awaits the owner's posting. posted: in the ledger (migration 031).
 */
export type InvoiceStatus = "draft" | "completed" | "posted";
/**
 * What Edit invoice opens: `edit` a draft in its editor, `amend` a completed invoice (with confirmation), `correct` a
 * posted one (a replacement the owner confirms), or `none`, with the reason shown next to the disabled action.
 */
export interface InvoiceEditDto { mode: "edit" | "amend" | "correct" | "none"; reason: string | null; note: string | null }
/** What an amendment of a completed invoice changes, returned with amend_confirm_required (details.amend). */
export interface InvoiceAmendSummary {
  before: { grand: Decimal; paid: Decimal }; after: { grand: Decimal; paid: Decimal };
  payments: { treasury: Named & { kind: string }; before: Decimal; after: Decimal }[];
  /** grand − paid after the change: left on the customer's or supplier's account (never collected or refunded silently). */
  onAccount: Decimal; paymentsChanged: boolean; shiftClosed: boolean;
}
export interface InvoiceListDto {
  id: string; kind: InvoiceKind; status: InvoiceStatus;
  /** Completed (number, stock and payments recorded) and by whom; null for a draft and for invoices completed and posted in one step before migration 031. */
  completedAt: string | null; completedByName: string | null; number: string | null; code: string | null; invoiceAt: string;
  /** null: a walk-in POS sale or refund (W-9), made with no customer and paid in full. */
  warehouse: Named; branch: Named; party: PartyRef | null; reference: string; note: string;
  /** `remaining` = grand − paid − `settled` (collections or payments settled against it, rule A-3). */
  totalBefore: Decimal; discount: Decimal; subtotal: Decimal; vat: Decimal; grand: Decimal; paid: Decimal; settled: Decimal; remaining: Decimal;
  /** Migration 049: tobacco municipality fees, apart from VAT (grand = subtotal + tobaccoFee + vat). */
  tobaccoFee: Decimal;
  linesCount: number; createdByName: string | null; createdAt: string; updatedAt: string; postedAt: string | null; postedByName: string | null;
  journalEntry: { id: string; number: string } | null; reversal: { id: string; number: string } | null; reversedAt: string | null; reversalAt: string | null;
  /** A refund's original invoice. */
  refundOf: InvoiceRef & { code: string | null } | null;
  legacyNumber: string | null;
  /** A POS sale or refund: the shift it was made in (W-2). */
  shift: { id: string; number: string } | null;
  /** The legal tax invoice number recorded from the compliant system (W-8), or null. */
  legalReference: string | null;
  /** A POS sale paid partly or wholly in cash: what the customer handed over and the change given back (W-10). */
  cashTendered: Decimal | null; cashChange: Decimal | null;
  /**
   * A replacement prepared by Correct: the invoice it corrects, with what was paid on it. While the replacement is a
   * draft the original is unchanged; posting the replacement (after confirming) reverses the original and posts the
   * replacement in one transaction.
   */
  corrects: InvoiceRef & { code: string | null; grand: Decimal; paid: Decimal; reversed: boolean } | null;
  /** A corrected invoice: its replacement (a draft until posted; the original is reversed only when it is posted). */
  correctedBy: InvoiceRef & { code: string | null; status: InvoiceStatus } | null;
  /**
   * A replacement: the original's payments it uses (part of `paid`, never taken or refunded again), up to its total.
   * "0.00" otherwise.
   */
  carried: Decimal;
  /** A sale saved as a draft at the register (POS); it is completed there, in the open shift. */
  posDraft: boolean;
}
/**
 * Correct a posted purchase or sale: prepare a replacement draft with its contents, dated `reversedAt` (the date the
 * original will be reversed on when the replacement is posted). Nothing is reversed yet.
 */
export interface InvoiceCorrectInput { reversedAt: string; statement?: string }
/** Posting a replacement needs `confirmCorrection`: it reverses the original in the same transaction. */
export interface InvoicePostInput { confirmCorrection?: boolean }
export interface InvoiceDto extends InvoiceListDto {
  discountPct: Decimal | null; lines: InvoiceLineDto[]; payments: InvoicePaymentDto[];
  /** The tobacco fee settings the invoice was worked out under (null: no fee charged). */
  tobacco: { feePct: Decimal; minimumFee: Decimal; priceIncludesFee: boolean; minimumBasis: import("./tobacco.js").TobaccoMinimumBasis;
    inclusiveShortfall: import("./tobacco.js").TobaccoShortfall; invoiceDiscount: import("./tobacco.js").TobaccoInvoiceDiscount; licenceNumber: string;
    /** How much the minimum fee added on top of fee-inclusive prices raised the total (case b), confirmed when the invoice was saved. */
    raised: Decimal;
    /** For an issued invoice or a correction's replacement: whether each item it was issued with charged the fee; any other item follows its own flag. */
    items?: Record<string, boolean> } | null;
  /** An invoice's refunds (drafts included). */
  refunds: (InvoiceRef & { code: string | null; status: InvoiceStatus; invoiceAt: string; grand: Decimal; reversed: boolean })[];
  /** The party's balance (posted lines tagged with it) now. */
  partyBalance: Decimal;
  /** What the party's completed invoices awaiting posting leave on account (not in partyBalance until posted). */
  partyPending: Decimal;
  /** What Edit invoice does for this invoice and the user. */
  edit: InvoiceEditDto;
  /** A sale made from a quotation (rule R-8), and a quotation's sales. */
  quotation: InvoiceRef & { code: string | null } | null;
  sales: (InvoiceRef & { code: string | null; status: InvoiceStatus })[];
  /** Collections or payments settled against this invoice (rule A-1), and what is still open for settling (A-3). */
  settlements: SettlementDto[];
  open: Decimal;
}
/** Totals are of posted, unreversed invoices; `awaitingPosting` counts completed ones the owner has not posted yet. */
export interface InvoicesDto { invoices: InvoiceListDto[]; grand: Decimal; paid: Decimal; remaining: Decimal; drafts: number; awaitingPosting: number }
export interface InvoiceOptionsDto {
  warehouses: (Named & { branchId: string })[];
  treasuries: (Named & { kind: string; branchId: string | null; accountId: string | null; commission: Decimal; commissionTaxRate: Decimal })[];
  /** Suppliers and customers; `pricing` is a customer's price list (wholesale, half or retail). */
  /** `balance`: posted lines; `pending`: what its completed invoices awaiting posting leave on account (same sign). */
  parties: (PartyRef & { accountId: string | null; balance: Decimal; pending: Decimal; taxNumber: string; phone: string; pricing: "wholesale" | "half" | "retail" | null })[];
  items: {
    id: string; code: string; nameEn: string; nameAr: string; categoryId: string; hasQty: boolean; taxTypeId: string | null; priceIncludesTax: boolean;
    /** Subject to the tobacco municipality fee (migration 049). */
    tobaccoFee: boolean;
    /** The picture's version for /api/items/:id/image?v=… (null: no picture). */
    imageAt: string | null;
    units: { id: string; nameEn: string; nameAr: string; pieces: Decimal; barcodes: string[]; priceWholesale: Decimal; priceHalf: Decimal; priceRetail: Decimal }[]
  }[];
  categories: Named[];
  taxTypes: (Named & { rate: Decimal })[];
  /** Quantity (base units) and value of each item in each warehouse in scope, for the average cost hint. `value` is null without the view_cost right. */
  positions: { warehouseId: string; itemId: string; qty: Decimal; value: Decimal | null }[];
  /** The user's selling price rights (migration 039): edit_price, approve_below_cost, view_cost. */
  salesRights: SalesRight[];
  /** Migration 049: the organization's tobacco fee settings, so the screens show the figures the server works out. */
  tobacco: import("./tobacco.js").TobaccoCalcSettings;
}

/** Selling price rights (migration 039), granted per user by the business owner; none implies another. */
export type SalesRight = "edit_price" | "approve_below_cost" | "view_cost";
export const SALES_RIGHTS: SalesRight[] = ["edit_price", "approve_below_cost", "view_cost"];
export interface SalesRightsDto {
  /** The current user's rights, and whether they may grant them (the owner). */
  mine: SalesRight[]; canManage: boolean;
  users: { id: string; name: string; phone: string; owner: boolean; administrator: boolean; rights: SalesRight[] }[];
}
/** A sales line below its stock's cost: its value after discounts, before tax, and the cost it leaves at (null without view_cost). */
export interface BelowCostLine {
  lineNo: number; itemId: string; itemUnitId: string; qty: Decimal; price: Decimal; discountPct: Decimal | null; discountAmount: Decimal | null; value: Decimal;
  /** The line's cost (four decimals, shown only with view_cost); the comparison itself is exact. */
  cost: Decimal | null;
  /** The stock is held at a zero value, so its cost is not known: the line needs an approval like a line below cost. */
  costUnknown?: boolean;
}
/** With a 409 below_cost_approval_* (error.belowCost): the sale's fingerprint and the lines below cost. */
export interface BelowCostDetails { fingerprint: string; lines: BelowCostLine[]; canApprove: boolean }
export type BelowCostStatus = "pending" | "approved" | "rejected" | "used";
export interface BelowCostApprovalDto {
  id: string; status: BelowCostStatus; warehouse: Named; party: { id: string; name: string } | null;
  requestedByName: string; requestedAt: string; decidedByName: string | null; decidedAt: string | null; reason: string | null;
  usedAt: string | null; invoiceId: string | null;
  /** Requested by the current user (only they can use it). */
  mine: boolean;
  lines: (BelowCostLine & { item: { code: string; nameEn: string; nameAr: string }; unit: { nameEn: string; nameAr: string } })[];
  /** The whole sale as proposed (requests from 30 Oct 2026 on; null for older ones): every line with its list price. */
  proposal: BelowCostProposal | null;
}
/**
 * The sale an approval request proposes, kept apart from the register's cart: each line's list price for the
 * customer's price list and unit (`listPrice`, null when the unit has none) next to the proposed price, and the totals.
 * No cost figure is part of it.
 */
export interface BelowCostProposal {
  lines: { lineNo: number; itemId: string; itemUnitId: string; item: { code: string; nameEn: string; nameAr: string }; unit: { nameEn: string; nameAr: string };
    qty: Decimal; listPrice: Decimal | null; price: Decimal; discountPct: Decimal | null; discountAmount: Decimal | null; tax: Decimal; fee: Decimal; total: Decimal }[];
  discountPct: Decimal | null; discountAmount: Decimal | null;
  totals: { subtotal: Decimal; vat: Decimal; fee: Decimal; grand: Decimal };
}
/**
 * The register gives a refused price back (POS pricing fix of 30 Oct 2026): the lines whose price was refused (no
 * edit_price) or not approved (below cost), and why. The server answers with each line's list price for the customer's
 * price list and the line's unit, and records the restore with the proposed prices. Nothing stored is changed.
 */
export interface PriceRestoreInput {
  partyId: string | null;
  lines: { lineNo: number; itemId: string; itemUnitId: string; price: string }[];
  outcome: "not_allowed" | "cancelled" | "rejected" | "not_approved";
  approvalId?: string | null;
}
export interface PriceRestoreDto { lines: { lineNo: number; itemUnitId: string; listPrice: Decimal | null }[] }
/** Asks for (or, with a reason, an approver gives) approval of a sale as it stands: the sale's form, and the invoice it completes or amends. */
/** A request always waits for another user who may approve: a reason sent here (by builds before the decisions of 29 Oct) is ignored. */
export interface BelowCostRequestInput { invoice?: InvoiceInput; invoiceId?: string | null }

/** The calculation's input per line: canonical decimal text, already checked. `inclusive`: the price includes VAT (sales, rule R-2). */
export interface CalcLine { qty: string; price: string; discountPct: string | null; discountAmount: string | null; taxRate: string; inclusive?: boolean; fee?: CalcFee | null }
/**
 * A line subject to the tobacco municipality fee (migration 049, docs/tobacco-fees.md): the fee percentage of the
 * product amount after discounts, at least `minimum` × `units`; `inclusive`: the entered price includes the fee;
 * `shortfall`: what happens when a fee-inclusive amount cannot cover the minimum (refuse the line, or charge the
 * minimum on top of the entered price). `units` are the units the minimum counts, as the organization's policy says.
 */
export interface CalcFee { pct: string; minimum: string; units: string; inclusive: boolean; shortfall: "refuse" | "minimum_on_top" }
export interface CalcRow {
  gross: string; lineDiscount: string; net: string; invoiceDiscount: string; taxable: string; tax: string; total: string;
  /** The tobacco fee (0.00 without one); `feeMinimum`: the minimum set it; `feeShortfall`: refused, the amount does not cover the minimum. */
  fee: string; feeMinimum: boolean; feeShortfall: boolean;
  /**
   * How much the line's total rises above what its fee-inclusive price gives, because that price could not cover the
   * minimum and the policy adds the minimum on top (case b): the fee and its VAT. "0.00" otherwise.
   */
  raised: string;
}
export interface CalcResult {
  rows: CalcRow[]; totalBefore: string; discount: string; subtotal: string; vat: string; grand: string; netBeforeInvoiceDiscount: string; invoiceDiscount: string;
  /** The tobacco fees, apart from the product amounts (subtotal) and VAT: grand = subtotal + fee + vat. */
  fee: string;
  /** The invoice discount had no line to apply to (every line charges the fee and the policy keeps them out of it). */
  discountNoBase: boolean;
  /** The sum of the lines' `raised`: grand − raised is the total at the entered prices. Never charged unconfirmed. */
  raised: string;
}
/** How the invoice discount is spread when lines charge the tobacco fee (policy, docs/tobacco-fees.md case c). */
export interface CalcOptions { invoiceDiscount?: "all_lines" | "exclude_fee_lines" }

/**
 * Rule B-2 (proposal P-1), in exact decimals, rounding to two decimals half away from zero:
 * gross = qty × price; the line discount is gross × pct ÷ 100, or the amount; the invoice discount is the sum of the
 * lines' nets × pct ÷ 100, or the amount, spread over the lines by net value with the last line taking what is
 * left; VAT = (net − share) × rate ÷ 100 per line; totals are sums of the rounded lines. A discount larger than its
 * base is not capped here: the server refuses it and the screen flags it.
 *
 * Rule R-2 adds lines whose price includes VAT (sales only). Such a line's discounts and share work on the entered
 * amounts as above, which fixes what the customer pays for it (`total`); its before-tax value is that total × 100 ÷
 * (100 + rate) and its VAT the rest. Its gross and discounts are then given before tax too (gross ÷ (1 + rate), the
 * line discount likewise, the invoice discount taking the rounding), so that for every line gross − line discount −
 * invoice discount = before tax, and the invoice's totals add up. `net` stays in the entered amounts (it is what the
 * screen shows and what the invoice discount is spread by).
 *
 * Migration 049 adds the tobacco municipality fee (docs/tobacco-fees.md) on lines that carry `fee`. The fee is worked
 * out on the product amount after the line's discount and its share of the invoice discount: the percentage of it,
 * at least the minimum × units. VAT is then charged on the product amount plus the fee. When the entered price
 * includes the fee, the amount (before VAT) is split into product and fee: product = amount × 100 ÷ (100 + pct),
 * unless the fee that leaves is below the minimum, then fee = minimum and product = the rest; the fee also comes off
 * the line's gross, so gross − discounts = product as before. `taxable` stays the product amount (sales revenue);
 * the fee is a separate amount owed to the municipality; grand = subtotal + fee + VAT. Every amount is rounded to two
 * decimals half away from zero per line, as above.
 */
export function invoiceCalc(lines: CalcLine[], discountPct: string | null, discountAmount: string | null, opts: CalcOptions = {}): CalcResult {
  const D = Exact.dec, r2 = (x: Exact.Dec) => Exact.roundHalfAway(x, 2), f = (x: Exact.Dec) => Exact.fmt(r2(x), 2), ZERO = D("0"), HUNDRED = D("100");
  const base = lines.map((l) => {
    const gross = r2(Exact.mul(D(l.qty), D(l.price)));
    const disc = l.discountPct !== null ? Exact.percentOf(gross, D(l.discountPct)) : l.discountAmount !== null ? r2(D(l.discountAmount)) : ZERO;
    return { gross, disc, net: Exact.sub(gross, disc), rate: D(l.taxRate), inclusive: !!l.inclusive, fee: l.fee ?? null };
  });
  const sumNet = Exact.sum(base.map((b) => b.net));
  const gen = discountPct !== null ? Exact.percentOf(sumNet, D(discountPct)) : discountAmount !== null ? r2(D(discountAmount)) : ZERO;
  // The lines the invoice discount is spread over: all of them, or (policy) only those without the tobacco fee.
  const takes = base.map((b) => opts.invoiceDiscount !== "exclude_fee_lines" || !b.fee);
  const spreadNet = Exact.sum(base.filter((_, i) => takes[i]).map((b) => b.net));
  const lastTaker = takes.lastIndexOf(true);
  let left = gen;
  const lineDisc: Exact.Dec[] = [], genDisc: Exact.Dec[] = [], fees: Exact.Dec[] = [];
  /** The fee on a product amount (fee excluded from it): the percentage, at least the minimum. */
  const feeOn = (fee: CalcFee, product: Exact.Dec) => {
    const pct = Exact.percentOf(product, D(fee.pct)), min = r2(Exact.mul(D(fee.minimum), D(fee.units)));
    return Exact.cmp(pct, min) >= 0 ? { fee: pct, minimum: false } : { fee: min, minimum: true };
  };
  /** Splits an amount that includes the fee into product and fee; null when it cannot cover the minimum. */
  const split = (fee: CalcFee, amount: Exact.Dec) => {
    const product = Exact.div(Exact.mul(amount, HUNDRED), Exact.add(HUNDRED, D(fee.pct)), 2), byPct = Exact.sub(amount, product);
    const min = r2(Exact.mul(D(fee.minimum), D(fee.units)));
    if (Exact.cmp(byPct, min) >= 0) return { product, fee: byPct, minimum: false };
    const rest = Exact.sub(amount, min);
    return rest.n > 0n ? { product: rest, fee: min, minimum: true } : null;
  };
  const rows: CalcRow[] = base.map((b, i) => {
    let share = ZERO;
    if (takes[i]) share = i === lastTaker ? left : spreadNet.n === 0n ? ZERO : Exact.div(Exact.mul(gen, b.net), spreadNet, 2);
    left = Exact.sub(left, share);
    const after = Exact.sub(b.net, share);
    let shortfall = false;
    if (!b.inclusive) {
      lineDisc.push(b.disc); genDisc.push(share);
      if (!b.fee) {
        const tax = Exact.percentOf(after, b.rate);
        fees.push(ZERO);
        return { gross: f(b.gross), lineDiscount: f(b.disc), net: f(b.net), invoiceDiscount: f(share), taxable: f(after), tax: f(tax), total: f(Exact.add(after, tax)), fee: "0.00", feeMinimum: false, feeShortfall: false, raised: "0.00" };
      }
      // The entered amount includes the fee: the product is what is left of it (and of the gross) once the fee is out.
      const s = b.fee.inclusive ? split(b.fee, after) : null;
      if (b.fee.inclusive && !s) shortfall = b.fee.shortfall === "refuse";
      const x = s ?? { product: after, ...feeOn(b.fee, after) };
      const gross = s ? Exact.sub(b.gross, s.fee) : b.gross;
      const tax = Exact.percentOf(Exact.add(x.product, x.fee), b.rate), total = r2(Exact.add(Exact.add(x.product, x.fee), tax));
      fees.push(x.fee);
      // The minimum added on top of a fee-inclusive price: the rise above that price's own total (with its VAT).
      const raised = b.fee.inclusive && !s && !shortfall ? Exact.sub(total, r2(Exact.add(after, Exact.percentOf(after, b.rate)))) : ZERO;
      return { gross: f(gross), lineDiscount: f(b.disc), net: f(b.net), invoiceDiscount: f(share), taxable: f(x.product), tax: f(tax), total: f(total),
        fee: f(x.fee), feeMinimum: x.minimum, feeShortfall: shortfall, raised: f(raised) };
    }
    const out = (x: Exact.Dec) => Exact.div(Exact.mul(x, HUNDRED), Exact.add(HUNDRED, b.rate), 2);
    const exVat = out(after), grossEx = out(b.gross), disc = Exact.sub(grossEx, exVat);
    // Before tax, the line discount is its own share of the discount, unless there is no invoice discount (then all of it).
    const ld = share.n === 0n ? disc : Exact.cmp(out(b.disc), disc) > 0 ? disc : out(b.disc), gd = Exact.sub(disc, ld);
    lineDisc.push(ld); genDisc.push(gd);
    if (!b.fee) {
      fees.push(ZERO);
      return { gross: f(grossEx), lineDiscount: f(ld), net: f(b.net), invoiceDiscount: f(gd), taxable: f(exVat), tax: f(Exact.sub(after, exVat)), total: f(after), fee: "0.00", feeMinimum: false, feeShortfall: false, raised: "0.00" };
    }
    // VAT included: the amount before VAT holds the product (and the fee, when the price includes it); VAT on both.
    const s = b.fee.inclusive ? split(b.fee, exVat) : null;
    if (b.fee.inclusive && !s) shortfall = b.fee.shortfall === "refuse";
    fees.push(s ? s.fee : feeOn(b.fee, exVat).fee);
    if (s) {
      return { gross: f(Exact.sub(grossEx, s.fee)), lineDiscount: f(ld), net: f(b.net), invoiceDiscount: f(gd), taxable: f(s.product), tax: f(Exact.sub(after, exVat)), total: f(after),
        fee: f(s.fee), feeMinimum: s.minimum, feeShortfall: false, raised: "0.00" };
    }
    // The fee comes on top of the entered price, with its own VAT.
    const x = feeOn(b.fee, exVat), feeTax = Exact.percentOf(x.fee, b.rate), tax = Exact.add(Exact.sub(after, exVat), feeTax);
    const raised = b.fee.inclusive && !shortfall ? Exact.add(x.fee, feeTax) : ZERO;
    return { gross: f(grossEx), lineDiscount: f(ld), net: f(b.net), invoiceDiscount: f(gd), taxable: f(exVat), tax: f(tax), total: f(Exact.add(Exact.add(after, x.fee), feeTax)),
      fee: f(x.fee), feeMinimum: x.minimum, feeShortfall: shortfall, raised: f(raised) };
  });
  const sum = (k: "gross" | "taxable" | "tax" | "fee") => Exact.fmt(Exact.sum(rows.map((r) => D(r[k]))), 2);
  const subtotal = sum("taxable"), vat = sum("tax"), fee = sum("fee");
  return {
    rows, totalBefore: sum("gross"), discount: Exact.fmt(Exact.add(Exact.sum(lineDisc), Exact.sum(genDisc)), 2), subtotal, vat,
    grand: Exact.fmt(Exact.add(Exact.add(D(subtotal), D(fee)), D(vat)), 2), netBeforeInvoiceDiscount: Exact.fmt(sumNet, 2), invoiceDiscount: Exact.fmt(gen, 2),
    fee, discountNoBase: gen.n !== 0n && lastTaker < 0, raised: Exact.fmt(Exact.sum(rows.map((r) => D(r.raised))), 2)
  };
}

/**
 * Rule B-6: a refunded quantity's share of the original line, at the original price, discounts and VAT: the
 * proportion of its before-tax value and VAT, rounded half away from zero; the refund that completes the line takes
 * what is left, so the refunds of a line add up to the line.
 */
export function refundShare(orig: ShareOf, earlier: ShareOf, qty: string): { gross: string; taxable: string; tax: string; total: string; cost: string; fee: string } {
  const D = Exact.dec;
  const completes = Exact.cmp(Exact.add(D(earlier.qty), D(qty)), D(orig.qty)) === 0;
  const part = (k: "gross" | "taxable" | "tax" | "cost" | "fee") => completes ? Exact.sub(D(orig[k] ?? "0"), D(earlier[k] ?? "0")) : Exact.div(Exact.mul(D(orig[k] ?? "0"), D(qty)), D(orig.qty), 2);
  const gross = part("gross"), taxable = part("taxable"), tax = part("tax"), fee = part("fee");
  return { gross: Exact.fmt(gross, 2), taxable: Exact.fmt(taxable, 2), tax: Exact.fmt(tax, 2), total: Exact.fmt(Exact.add(Exact.add(taxable, fee), tax), 2), cost: Exact.fmt(part("cost"), 2), fee: Exact.fmt(fee, 2) };
}
/** A line's (or its earlier refunds') quantity and values; `cost` (the stock value a sale took out) for rule R-6; `fee` the tobacco fee (migration 049). */
export interface ShareOf { qty: string; gross: string; taxable: string; tax: string; cost?: string; fee?: string }

// ---------------------------------------------------------------- Phase 7c: settlements and opening balances
// Settling posted collections and payments against posted invoices (rules A-1 to A-5) and opening balances of
// clients and suppliers (rules O-1 to O-4), docs/proposed-financial-rules.md, section 8. PROVISIONAL.

/** A collection or payment settled against an invoice (legacy Settlement). No journal entry: the ledger already has both. */
export interface SettlementDto {
  id: string; amount: Decimal; createdAt: string; createdByName: string | null;
  voucher: VoucherRef & { code: string | null; voucherAt: string };
  /** Exactly one of the two: a Delta invoice, or an open item of the migration cutover (CV-6). */
  invoice: (InvoiceRef & { code: string | null; invoiceAt: string; grand: Decimal }) | null;
  openItem: MigratedOpenItemRef | null;
}
/** What a client or supplier still owed, or was owed, at the migration cutover (CV-4), as the cutover file states it. */
export interface MigratedOpenItemRef {
  id: string; side: "debit" | "credit"; reference: string; itemAt: string; amount: Decimal;
  /** The legacy document, as stored: its table, kind and number. */
  document: { table: string; kind: string | null; number: string | null } | null;
}
export interface MigratedOpenItemDto extends MigratedOpenItemRef { settled: Decimal; open: Decimal }
/** An invoice that can still be settled from a voucher: posted, unreversed, of the voucher's party, with something open. */
export interface OpenInvoiceDto extends InvoiceRef { code: string | null; invoiceAt: string; grand: Decimal; paid: Decimal; credited: Decimal; settled: Decimal; open: Decimal }
export interface VoucherSettlementsDto {
  voucher: VoucherRef & { code: string | null; total: Decimal; settled: Decimal; unsettled: Decimal };
  settlements: SettlementDto[];
  /** Oldest first, the order rule A-4 settles in. Empty when the caller may not settle. */
  open: OpenInvoiceDto[];
  /** The party's migrated open items on the voucher's side (CV-6), oldest first. Empty when the caller may not settle. */
  openItems: MigratedOpenItemDto[];
}
/** Each allocation names one invoice or one migrated open item. */
export interface SettleInput { allocations: { invoiceId?: string; openItemId?: string; amount: DecimalInput }[] }

// ---------------------------------------------------------------- Phase 9: reports
// Financial statements, tax, invoice, treasury and warehouse reports of the audited reports hub, read from posted
// documents and the ledger. The rules F-1 to F-6 (docs/proposed-financial-rules.md, section 9) are PROVISIONAL.
// F-2: a reversed document appears twice, as itself on its date and as a negative "reversal" row on its reversal date,
// so a report agrees with the ledger for any period.

export interface PartyRef { id: string; kind: PartyKind; name: string }
export interface ItemRef { id: string; code: string; nameEn: string; nameAr: string }

/** One account in a financial statement, in its natural sign (F-1): debit balances for assets and expenses, credit balances for the rest. */
export interface FsRow { account: AccountRef; typeId: string; amount: Decimal }
/** A group's subtotal in a statement section, every account beneath it counted once (rule H-6); tree order. */
export interface FsGroupRow { group: GroupRef & { parentId: string | null; depth: number }; amount: Decimal }
export interface FsSection { level1Id: number; nameEn: string; nameAr: string; rows: FsRow[]; groups: FsGroupRow[]; total: Decimal }
export interface IncomeStatementDto { from: string | null; to: string | null; revenues: FsSection; expenses: FsSection; netProfit: Decimal }
/** Cumulative as at `asAt` (P-15). `profit` is revenues less expenses not yet closed into equity. */
export interface BalanceSheetDto {
  asAt: string | null; assets: FsSection; liabilities: FsSection; equity: FsSection; profit: Decimal;
  totalAssets: Decimal; totalLiabilitiesEquity: Decimal; difference: Decimal;
  /** Total liabilities, and total equity: the equity accounts plus `profit` (the results not closed into equity), once. */
  totalLiabilities: Decimal; totalEquity: Decimal;
  /** `profit` split at `periodFrom` (the start of the current period): results of earlier periods not closed into equity, and the current period's. */
  periodFrom: string | null; priorResult: Decimal; currentResult: Decimal;
  /** The branch filter: entries of that branch's documents only; `unbranched` counts entries left out because they have no branch. */
  branch: Named | null; unbranched: number;
}

export interface TaxReturnRow { source: "invoices" | "commissions" | "assets" | "expenses"; taxType: Named; rate: Decimal; taxable: Decimal; tax: Decimal }
export interface TaxReturnSide { rows: TaxReturnRow[]; taxable: Decimal; tax: Decimal }
/** Output VAT less input VAT (F-4), with the period's movement on the tax types' accounts as a check. */
export interface TaxReturnDto { from: string | null; to: string | null; output: TaxReturnSide; input: TaxReturnSide; net: Decimal; ledger: Decimal; difference: Decimal }

export interface ReportOptionsDto {
  branches: Named[]; warehouses: (Named & { branchId: string })[]; treasuries: (Named & { branchId: string; kind: "cash" | "bank" })[];
  parties: PartyRef[]; users: { id: string; name: string }[]; items: ItemRef[]; categories: Named[]; types: GroupOption[];
}

export type ReportInvoiceKind = Exclude<InvoiceKind, "quotation">;
export interface InvoiceReportRow {
  id: string; kind: ReportInvoiceKind; code: string | null; at: string; reversal: boolean; warehouse: Named; branch: Named; party: PartyRef | null;
  subtotal: Decimal; vat: Decimal; grand: Decimal; paid: Decimal; remaining: Decimal; byName: string | null;
  /** Tobacco municipal fees (migration 049): subtotal + tobaccoFee + vat = grand. */
  tobaccoFee: Decimal;
}
export interface InvoiceReportDto { from: string | null; to: string | null; rows: InvoiceReportRow[]; subtotal: Decimal; vat: Decimal; grand: Decimal; paid: Decimal; remaining: Decimal; tobaccoFee: Decimal }

export type DetailKind = ReportInvoiceKind | "beginning_inventory" | "corrupted";
export interface InvoiceDetailRow {
  id: string; kind: DetailKind; code: string | null; at: string; reversal: boolean; warehouse: Named; item: ItemRef; unit: Named;
  side: "in" | "out"; qty: Decimal; price: Decimal; tax: Decimal; total: Decimal; byName: string | null;
}
export interface InvoiceDetailsDto { from: string | null; to: string | null; rows: InvoiceDetailRow[]; inTax: Decimal; inTotal: Decimal; outTax: Decimal; outTotal: Decimal }

export interface InvoiceProfitRow {
  id: string; kind: "sale" | "sale_refund"; code: string | null; at: string; reversal: boolean; branch: Named; warehouse: Named; item: ItemRef; unit: Named;
  qty: Decimal; sales: Decimal; cost: Decimal; profit: Decimal;
}
export interface InvoiceProfitsDto { from: string | null; to: string | null; rows: InvoiceProfitRow[]; sales: Decimal; cost: Decimal; profit: Decimal }

export type MoneyKind = "collect" | "payment" | "deposit" | "withdraw" | "expense";
export interface MoneyReportRow {
  id: string; kind: MoneyKind; code: string | null; at: string; reversal: boolean; branch: Named | null; party: PartyRef | null; account: AccountRef | null;
  debit: Decimal; credit: Decimal; balance: Decimal; note: string; byName: string | null;
}
export interface MoneyReportDto { from: string | null; to: string | null; rows: MoneyReportRow[]; debit: Decimal; credit: Decimal; balance: Decimal }

export interface TransferReportRow {
  id: string; code: string | null; at: string; reversal: boolean; fromTreasury: Named; toTreasury: Named; amount: Decimal;
  depositNumber: string; depositDate: string | null; note: string; byName: string | null;
}
export interface TransferReportDto { from: string | null; to: string | null; rows: TransferReportRow[]; amount: Decimal }

/** The source of a journal entry: a voucher, invoice or stock document kind, or a manual entry ("journal"). A reversal counts with what it reverses. */
export type MovementSource = VoucherKind | InvoiceKind | StockKind | "journal";
export interface MovementSummaryRow { source: MovementSource; treasury: Named; count: number; debit: Decimal; credit: Decimal; net: Decimal }
export interface MovementSummaryDto { from: string | null; to: string | null; rows: MovementSummaryRow[]; debit: Decimal; credit: Decimal; net: Decimal }

export interface WarehouseBalanceRow { warehouse: Named; branch: Named; items: number; value: Decimal }
export interface WarehouseBalancesDto { rows: WarehouseBalanceRow[]; items: number; value: Decimal }
export interface WarehouseItemRow { warehouse: Named; branch: Named; item: ItemRef; category: Named; unit: Named; qty: Decimal; average: Decimal; value: Decimal }
export interface WarehouseItemsDto { rows: WarehouseItemRow[]; value: Decimal }

// ---------------------------------------------------------------- Phase 9b: home dashboard
// The approved prototype's home page from posted documents. Each part is null when the user lacks its key.
export interface DashKpi { count: number; amount: Decimal; previous: Decimal }
export interface DashMonth { month: string; income: Decimal; expenses: Decimal }
export interface DashRecent { id: string; code: string | null; at: string; party: PartyRef | null; grand: Decimal; remaining: Decimal; paid: Decimal }
export interface DashboardDto {
  month: string; branchId: string | null;
  kpis: { sale: DashKpi | null; purchase: DashKpi | null; sale_refund: DashKpi | null; purchase_refund: DashKpi | null; customers: number | null; suppliers: number | null };
  /** The six months ending with `month`: revenues and expenses from the ledger (r2incomeStatement). */
  months: DashMonth[] | null;
  topItems: { item: ItemRef; sales: Decimal }[] | null;
  recent: DashRecent[] | null;
  treasuries: { treasury: Named & { kind: "cash" | "bank" }; balance: Decimal }[] | null;
}

// ---------------------------------------------------------------- Phase 10b: printers, partners, barcode print, client area
/** A printer's settings (legacy Printer). Configuration only: Delta sends nothing to a printer in this phase. */
export interface PrinterInput { name: string; ip: string; port: number; marginX: DecimalInput; marginY: DecimalInput; paperWidth: DecimalInput }
export interface PrinterRecordDto { id: string; name: string; ip: string | null; port: number; marginX: Decimal; marginY: Decimal; paperWidth: Decimal; usersCount: number; createdAt: string; updatedAt: string }

/** A partner (legacy Partner): a sub-ledger tag on an owner's equity account (rules G-1 to G-3, provisional). */
export interface PartnerRef { id: string; name: string; accountId: string }
export interface PartnerInput { name: string; phone: string; accountId: string }
export interface PartnerDto {
  id: string; name: string; phone: string; account: AccountRef;
  /** Posted lines tagged with the partner, credit minus debit: positive is what the business owes the partner. */
  balance: Decimal; linesCount: number; vouchersCount: number; createdAt: string; updatedAt: string;
}
export interface PartnerOptionsDto { accounts: AccountRef[] }

/** One row of the barcode print screen (prototype /items/qrPrint): an item unit, its first barcode ("" when none) and its retail price. */
export interface BarcodeUnitDto { itemUnitId: string; item: Named; category: Named; unit: Named; barcode: string; barcodes: string[]; price: Decimal }
export interface BarcodePrintDto { units: BarcodeUnitDto[]; categories: Named[]; organization: Named }

/** Client area: the organization's account and what it uses. Plans, prices and payment are not built (owner decision pending). */
export interface ClientAreaDto {
  organization: { id: string; nameEn: string; nameAr: string; phone: string; email: string; fax: string; landline: string; taxNumber: string; commercialRecord: string; country: string; city: string; status: "pending" | "active" | "expired" | "suspended" | "rejected"; createdAt: string };
  usage: { users: number; maxUsers: number; branches: number; warehouses: number; treasuries: number };
  /** A business that signed up: its plan (or the no-subscription limits). Null for organizations Delta provisioned. */
  entitlements?: import("./platform.js").EntitlementsDto | null;
}

// ---------------------------------------------------------------- Phase 11a: the POS register (online only)
/** A shift (W-2, W-3): a cashier's drawer from opening to closing, counted against one cash treasury. */
export interface ShiftDto {
  id: string; number: string; branch: Named; user: { id: string; name: string }; treasury: Named & { kind: "cash" | "bank" };
  openedAt: string; openingFloat: Decimal; closedAt: string | null; closedByName: string | null;
  /**
   * Live for an open shift; as recorded for a closed one. Float + the shift's cash sales − its cash refunds + the
   * drawer's other cash inflows − its outflows (W-3 as amended by W-11, `drawer`).
   */
  expected: Decimal; counted: Decimal | null; difference: Decimal | null; note: string;
  /** Posted POS sales and refunds of the shift; ones reversed before it closed are left out (their reversal undoes them). */
  sales: { count: number; total: Decimal }; refunds: { count: number; total: Decimal };
  /** What each treasury received (sales) less what it paid out (refunds), in this shift. */
  byTreasury: { treasury: Named & { kind: "cash" | "bank" }; received: Decimal; paidOut: Decimal }[];
  /**
   * W-11: the drawer's other cash movements in the shift (counted ones only), in and out. `complete` false: some cash on
   * the drawer's account could not be attributed (`unresolved`: movements made while another shift was open on the
   * treasury; `unattributed`: manual or migrated lines), so the difference is not a reconciliation.
   */
  drawer: { inflows: Decimal; outflows: Decimal; complete: boolean; unresolved: { count: number; amount: Decimal }; unattributed: { count: number; amount: Decimal } };
}
/**
 * A cash movement of a shift's drawer outside the shift's own POS documents (W-11): a voucher row on the drawer's
 * treasury, either side of a cash transfer, an invoice payment row on it (ERP or another shift), or an asset purchase
 * paid from it. A document's posting and its reversal are separate events (`event`), each at its own instant and
 * in the shift open then. `counted` false with `reason` "shared_drawer": another shift was open on the same treasury at
 * that instant (possible only in data from before W-14), so it cannot be given to either.
 */
export interface DrawerMovementDto {
  source: "voucher" | "invoice" | "asset_invoice"; kind: string; id: string; code: string | null; event: "posted" | "reversal"; at: string;
  direction: "in" | "out"; amount: Decimal; counted: boolean; reason: "shared_drawer" | null;
}
export interface ShiftOpenInput { treasuryId: string; openingFloat: DecimalInput }
export interface ShiftCloseInput { counted: DecimalInput; note?: string }
/** A POS sale (W-1, W-4): `requestId` is made by the register once per sale and sent again on every retry. */
export interface PosSaleInput {
  /** No `partyId`: a walk-in sale (W-9), paid in full. */
  requestId: string; warehouseId: string; partyId?: string | null;
  discountPct?: DecimalInput | null; discountAmount?: DecimalInput | null; note?: string;
  lines: InvoiceLineInput[]; payments: InvoicePaymentInput[];
  /** The cash the customer handed over (W-10); at least the sale's cash payments. Change = tendered − cash payments. */
  cashTendered?: DecimalInput | null;
  /** Completing a POS draft (saved with POST /pos/drafts): its id. The draft is posted with these lines and payments. */
  draftId?: string | null;
  /** The revised total the cashier confirmed when the minimum tobacco fee raises it (InvoiceInput.confirmTobaccoTotal). */
  confirmTobaccoTotal?: string | null;
}
/** A cart saved at the register as a draft (W-12): no payments, no shift, nothing moved until it is completed. */
export interface PosDraftInput {
  warehouseId: string; partyId?: string | null; discountPct?: DecimalInput | null; discountAmount?: DecimalInput | null; note?: string;
  lines: InvoiceLineInput[];
}
/** A POS refund (W-5) of a posted sale, inside the cashier's open shift, paid back in full from `treasuryId`. */
export interface PosRefundInput { requestId: string; invoiceId: string; note?: string; lines: { lineNo: number; qty: DecimalInput }[]; treasuryId: string }
/** `replayed`: the request was already done; this is the invoice it made, and nothing was posted again. */
export interface PosResultDto { invoice: InvoiceDto; replayed: boolean }
/** Hands the owner role to (or shares it with) another administrator, or gives it up while another owner remains. */
export interface UserOwnerInput { owner: boolean }
/**
 * The seller on receipts and shift reports (W-6). `logo`: the organization logo's version (its update time) when one
 * is set, used to ask for /api/organization/logo?v=… so a replaced logo is never served from a stale cache.
 */
export interface PosSeller { nameEn: string; nameAr: string; taxNumber: string; address: string; legalInvoiceSystem: string; logo: string | null }
type TreasuryKindRef = Named & { kind: "cash" | "bank" };
/**
 * A shift's report (open or closed; the same report is reprinted after closing). Every figure comes from the shift's
 * stored invoices and payments; `methods[].ledger` is the same movement read back from the posted journal lines on
 * the treasury's account, so a difference between the two shows up instead of being hidden.
 */
export interface ShiftReportDto {
  shift: ShiftDto; seller: PosSeller; testDocument: boolean; generatedAt: string;
  /** Completed or posted, unreversed sales: count, before discount, discount, VAT, total; `walkIn` of them without a customer (W-9). */
  sales: { count: number; walkIn: number; totalBefore: Decimal; discount: Decimal; vat: Decimal; total: Decimal; /** Tobacco municipal fees (migration 049), apart from VAT. */ tobaccoFee: Decimal };
  refunds: { count: number; vat: Decimal; total: Decimal; tobaccoFee: Decimal };
  /** Sales total − refunds total. */
  net: Decimal;
  /** What named customers left on account (sales total − what was paid at the till). */
  onAccount: Decimal;
  /** Cash handed over and change given back (W-10); the change never reaches the ledger. */
  tendered: Decimal; change: Decimal;
  /**
   * Per payment method (treasury): what sales took (gross), card commission with its tax, what refunds paid out, the
   * net into the treasury, and the ledger's figure for the same invoices (`matches`: equal to the cent).
   * `pending`: the part of `net` from invoices completed at the till that the owner has not posted yet: real money in
   * the drawer or bank, not yet in the ledger; `matches` compares the ledger with `net` − `pending`.
   */
  methods: { treasury: TreasuryKindRef; sales: Decimal; commission: Decimal; refunds: Decimal; net: Decimal; pending: Decimal; ledger: Decimal; matches: boolean }[];
  /** Completed sales and refunds of the shift awaiting the owner's posting (they count in every figure above). */
  awaitingPosting: number;
  /**
   * The drawer (the shift's cash treasury): float + net cash sales − cash refunds + other inflows − other outflows =
   * expected (W-3, W-11); counted and the difference once closed.
   */
  cash: { openingFloat: Decimal; sales: Decimal; refunds: Decimal; inflows: Decimal; outflows: Decimal; expected: Decimal; counted: Decimal | null; difference: Decimal | null };
  /**
   * W-11: every cash movement attributed to the drawer's treasury during the shift, counted or not (with why), and the
   * journal lines on the drawer's ledger account that name no treasury (manual or migrated entries): never counted.
   */
  drawer: { movements: DrawerMovementDto[]; inflows: Decimal; outflows: Decimal; unattributed: { count: number; amount: Decimal }; unresolved: { count: number; amount: Decimal }; complete: boolean };
  invoices: { id: string; code: string; kind: "sale" | "sale_refund"; status: InvoiceStatus; invoiceAt: string; party: PartyRef | null; grand: Decimal; reversed: boolean;
    payments: { treasury: TreasuryKindRef; amount: Decimal }[]; tendered: Decimal | null; change: Decimal | null }[];
}
/**
 * A customer's history: Delta's own sales and refunds (posted and drafts, newest first) and the legacy documents kept
 * from the old system for this customer, as stored. Legacy amounts are the stored fields verbatim (SubTotal, Discount,
 * Tax, PaidUp, PaidWallet, PaidLater); Delta does not work out a total or an outstanding amount from them, because the
 * legacy meaning of those fields is not confirmed (docs/migration-trial-import.md).
 */
/** One page of a list, with the count of everything the list holds (not only this page). */
export interface Paged<T> { rows: T[]; total: number; page: number; pageSize: number }
/**
 * A customer's history (acceptance pass, D-3, W-12). Every amount is the stored value with three decimals (the
 * ledger's scale), never rounded: imported history may carry a third decimal.
 */
export interface CustomerHistoryDto {
  party: PartyRef & { phone: string };
  /**
   * The customer's ledger balance (debit minus credit of every posted line naming them, the customer statement's
   * closing balance), and the part of it from migrated (legacy) ledger lines.
   */
  ledger: { balance: Decimal; migrated: Decimal };
  /** Posted and reversed sales and refunds, newest first, a page at a time. Drafts are apart (`drafts`). */
  native: Paged<CustomerHistoryDocDto>;
  /** Drafts (never in a total), newest first; `total` counts them all. */
  drafts: Paged<CustomerHistoryDocDto>;
  /**
   * Over every posted, unreversed sale and refund in the user's scope (not only the page shown). For sales: their
   * total, what was paid at the sale, what was unpaid at the sale, refunds (paid back in cash and credited to the
   * account), collections allocated to them, and what is open (rule A-3, never below zero; `openRaw` before that).
   */
  totals: {
    sales: { count: number; total: Decimal; paidAtSale: Decimal; unpaidAtSale: Decimal; refunded: Decimal; refundPaidBack: Decimal; refundCredited: Decimal; allocated: Decimal; open: Decimal; openRaw: Decimal };
    refunds: { count: number; total: Decimal; paidBack: Decimal; credited: Decimal };
  };
  /**
   * How the ledger balance is made up, by where each posted line came from (reversals go with what they reverse),
   * and how the open amount on invoices leads to it. Null when the user's scope hides documents of this customer:
   * a partial history cannot be reconciled with the whole ledger.
   */
  reconciliation: {
    bySource: { source: "sale" | "sale_refund" | "collect" | "opening_balance" | "manual" | "migrated" | "other"; amount: Decimal }[];
    /** Open on invoices (A-3, before the zero floor) + opening balances + journals + migrated + other − collections not allocated to invoices = balance. */
    openOnInvoices: Decimal; collections: Decimal; allocatedToInvoices: Decimal; allocatedToOpenItems: Decimal; unallocatedCollections: Decimal;
    computed: Decimal; difference: Decimal;
  } | null;
  legacy: Paged<{ id: string; table: string; kind: string | null; number: string | null; documentAt: string | null; fields: Record<string, string | null> }> & {
    /** Sums of the stored numeric fields over every legacy document of the customer in scope, exactly as stored. */
    sums: Record<string, string>;
  };
  /** Legacy documents held in this organization at all: 0 means the legacy migration has not been run here. */
  legacyInOrganization: number;
  /** Documents outside the user's warehouses are not listed or totalled; this says how many were left out. */
  hiddenByScope: { native: number; legacy: number };
}
export interface CustomerHistoryDocDto {
  id: string; kind: "sale" | "sale_refund"; code: string | null; status: InvoiceStatus; invoiceAt: string; reversed: boolean; pos: boolean;
  refundOf: { id: string; code: string } | null;
  grand: Decimal;
  /** Paid at the sale (for a refund: paid back from a treasury). */
  paid: Decimal;
  /** Sales: grand − paid. Refunds: grand − paid, credited to the customer's account. */
  unpaid: Decimal;
  /** Sales only (zero for refunds): refunds of it, what they paid back and what they credited to the account. */
  refunded: Decimal; refundPaidBack: Decimal; refundCredited: Decimal;
  /** Sales only: collections allocated to it (settlements, A-1 to A-5). */
  allocated: Decimal;
  /** Sales only: what is open for settlement (A-3: unpaid − refund credits − allocations, never below zero). */
  open: Decimal;
  /** Sales only: refund credits and allocations beyond what was unpaid, which stay as credit on the account. */
  excessCredit: Decimal;
}
export interface PosStateDto {
  shift: ShiftDto | null;
  /** The customer a sale is with when none is chosen (W-1); null until set (PUT /pos/customer, key `setting`). */
  posCustomer: PartyRef | null;
  /** For the receipt (W-6). `legalInvoiceSystem`: the compliant system that issues the legal tax invoices (non-fiscal POS). */
  seller: PosSeller;
  /** Cash treasuries in the user's scope, to open a shift against. */
  cashTreasuries: (Named & { branchId: string })[];
  /** The server is not production (staging, development, tests): every receipt prints TEST DOCUMENT. */
  testDocument: boolean;
  /**
   * W-16: the register's Cash and mada / Network buttons for the open shift (null with no shift): the treasury each
   * completes the sale with, from Settings › POS payment buttons, or why the button is disabled.
   */
  quickPay: { cash: QuickButtonDto; card: QuickButtonDto } | null;
}

// ---------------------------------------------------------------- POS quick payment buttons (W-16, migration 032)
export type QuickMethod = "cash" | "card";
/**
 * Why a quick button cannot be used: not set up for the branch; the treasury outside the user's scope; no longer of
 * the right kind (cash / bank) or branch; no ledger account; for Cash, a cash box
 * other than the one the shift was opened on.
 */
export type QuickProblem = "not_configured" | "no_access" | "treasury_kind" | "treasury_branch" | "treasury_no_account" | "cash_other_drawer";
export interface QuickButtonDto { method: QuickMethod; treasury: (Named & { kind: "cash" | "bank" }) | null; useShiftDrawer: boolean; problem: QuickProblem | null }
export interface QuickSettingDto { treasury: (Named & { kind: "cash" | "bank" }) | null; useShiftDrawer: boolean; problem: Exclude<QuickProblem, "no_access" | "cash_other_drawer"> | null; updatedAt: string | null; updatedBy: string | null }
export interface PosQuickSettingsDto {
  branches: { branch: Named; cash: QuickSettingDto; card: QuickSettingDto }[];
  /** Treasuries the editor may choose: of the branch, in the editor's scope. */
  treasuries: (Named & { kind: "cash" | "bank"; branchId: string; hasAccount: boolean; commission: Decimal; commissionAccount: boolean })[];
}
/** Clears the button with treasuryId null and useShiftDrawer false. */
export interface PosQuickInput { treasuryId: string | null; useShiftDrawer?: boolean }

// ---------------------------------------------------------------- direct printing (phase 12a; docs/direct-printing.md)

export type PrintOutcome = "requested" | "queued" | "spooled" | "done" | "failed" | "unknown" | "cancelled" | "confirmed" | "browser";
export type PrintLayout = "receipt_58" | "receipt_80" | "a4";
/** One print of a document: the original (copy 0) or a numbered copy, where it was printed and what is known of it. */
export interface PrintRecordDto {
  id: string; invoiceId: string; copyNo: number; jobId: string; channel: "agent" | "browser"; layout: PrintLayout; workstation: string;
  requestedBy: string; requestedAt: string; outcome: PrintOutcome; outcomeAt: string | null; outcomeDetail: string | null;
}
export interface PrintClaimInput { jobId: string; channel: "agent" | "browser"; layout: PrintLayout; workstation: string; copy: boolean }
export interface PrintOutcomeInput { outcome: Exclude<PrintOutcome, "requested">; detail?: string }
/** A sale's legal tax invoice number from the organization's compliant system (non-fiscal POS; docs/non-fiscal-pos.md). */
export interface ExternalInvoiceRefDto { id: string; reference: string; recordedBy: string; recordedAt: string; voidedAt: string | null; voidNote: string | null }
/**
 * What a printed invoice states besides the invoice itself (GET /invoices/:id/print-header): the seller from Settings ›
 * Organization, the client's or supplier's details, and whether this server prints test documents (any server
 * that is not production: staging, development, tests). Readable by whoever may see the invoice.
 */
export interface InvoicePrintHeaderDto {
  seller: {
    nameEn: string; nameAr: string; taxNumber: string; commercialRecord: string; phone: string; email: string;
    /** The address lines as entered: street and building number, neighborhood, city and postal code, country. */
    address: string[]; hasLogo: boolean; legalInvoiceSystem: string;
  };
  party: { name: string; taxNumber: string; commercialRecord: string; phone: string; address: string };
  testDocument: boolean;
}
export interface InvoicePrintsDto {
  prints: PrintRecordDto[];
  /** The name of the compliant system that issues the legal tax invoices (Settings › Organization), or null. */
  legalInvoiceSystem: string | null;
  externalRefs: ExternalInvoiceRefDto[];
}

// ---------------------------------------------------------------- Windows Print Agent package (docs/print-agent-package.md)
/** A package Delta can serve: its file is present and matches its SHA-256. */
export interface AgentPackageDto {
  version: string; arch: "x64"; file: string; size: number; sha256: string;
  /** False until a code-signing certificate is available: the page and the package say so. */
  signed: boolean; node: string | null; builtAt: string | null; requires: string | null;
  /** Same-origin download address (needs the printers key). */
  url: string;
  /** The release's own checksum (manifest.json). `sha256` and `size` are those of the file served, which differs only
   *  by delta-address.txt when `deltaAddress` is set (this Delta's address, so Setup opens with it filled in). */
  releaseSha256: string;
  deltaAddress: string | null;
}
export interface AgentDownloadDto {
  windows: AgentPackageDto | null;
  /** Why there is no Windows package on this server: not built here, the file is missing, or it fails its checksum. */
  windowsReason: "not_built" | "manifest_invalid" | "file_missing" | "checksum_mismatch" | null;
  /** No macOS package exists. */
  macos: null;
}

/** Delta POS for Windows, the desktop app's installer (docs/desktop-app.md): what this Delta offers for download and updates. */
export interface DesktopPackageDto {
  version: string; arch: "x64"; file: string; size: number; sha256: string;
  /** False until a code-signing certificate is available: the page, the app and the installer say so. */
  signed: boolean; builtAt: string | null; requires: string | null;
  /** Same-origin download address (any signed-in user: the app's own update check uses it). */
  url: string;
}
export interface DesktopDownloadDto {
  windows: DesktopPackageDto | null;
  windowsReason: "not_built" | "manifest_invalid" | "file_missing" | "checksum_mismatch" | null;
}

/** Migration 056: the business default for an expense's input tax; `canChange`: the caller may set it. */
export interface ExpenseTaxDefaultDto { recoverable: boolean | null; canChange: boolean }
